import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  CandlestickSeries,
  HistogramSeries,
  ColorType,
  createChart,
  createSeriesMarkers,
  CrosshairMode,
  LineStyle,
  TickMarkType,
} from "lightweight-charts";
import { useIsMobile } from "@/hooks/useIsMobile";
import type {
  CandlestickData,
  IChartApi,
  ISeriesApi,
  ISeriesMarkersPluginApi,
  SeriesMarker,
  Time,
  UTCTimestamp,
} from "lightweight-charts";

type BouncePriceLineHandle = ReturnType<
  ISeriesApi<"Candlestick">["createPriceLine"]
>;

import { TokenInfoBar } from "@/components/TokenInfoBar";
import { NarrativePanel } from "@/components/NarrativePanel";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { WalletConnectModal } from "@/components/WalletConnectModal";
import { TraderProfileTooltip } from "@/components/TraderProfileTooltip";
import { ChartQuickTrade } from "@/components/ChartQuickTrade";

import {
  Zap,
  Target,
  ShieldCheck,
} from "lucide-react";
import {
  useApp,
  type ChartCandleBarSummary,
  type ChartTapeSummary,
  type UserBounceZone,
} from "@/context/AppContext";
import { useSocket } from "@/context/SocketContext";
import { useWallet } from "@/context/WalletContext";
import { usePumpPortalTrades } from "@/hooks/usePumpPortalTrades";
import { useWalletExecutionMarkers } from "@/hooks/useWalletExecutionMarkers";
import { useSvixEngine } from "@/hooks/useSvixEngine";
import { BUILTIN_SCALPER_PRESET_ID } from "@/lib/algorithmPresets";
import {
  CHART_INTERVALS,
  PUMP_CANDLES_MAX_LIMIT,
  chartIntervalBucketSec,
  fetchPumpCandles,
  fetchPumpCandlesPaged,
  getCachedCandles,
  setCachedCandles,
  prefetchBatchCandles,
  mergeBaseCandles,
  mergeLiveMcUsdIntoCandles,
  pumpPortalMcScaleFactor,
  type ChartInterval,
} from "@/lib/pumpCandles";
import { formatUsdCompact } from "@/lib/formatUsd";
import {
  effectiveChartTimeZone,
  formatChartCrosshairTime,
  formatChartTickMark,
  loadChartTimezoneChoice,
  type ChartTimezoneChoice,
} from "@/lib/chartTimezone";
import {
  reduceScalperPaper,
  type BotTradeRowChain,
} from "@/lib/scalperPaperEngine";
import { SCALPER_PAPER_CONFIG } from "@/lib/scalperPaperConfig";
import {
  appendPumpPortalTradingWalletHint,
  getEffectivePumpPortalApiKey,
  getPumpPortalTradingWalletPubkey,
} from "@/lib/pumpPortalConfig";
import { fetchWalletSolDeltaSol } from "@/lib/solanaTxSolDelta";
import {
  detectBounceZones,
  MIN_BOOTSTRAP_CANDLES,
} from "@/lib/chartBounceZones";
import {
  inferPumpPortalTradePool,
  postPumpPortalLocalTradeWithFallback,
  confirmLightningTx,
} from "@/lib/pumpPortalLightningTrade";
import { detectBounceZonesVision } from "@/lib/visionBounceDetect";
import { BUILTIN_REVIVAL_HUNTER_PRESET_ID } from "@/lib/algorithmPresets";
import { reduceRevivalHunter } from "@/lib/revivalHunterEngine";
import * as nursery from "@/lib/nurseryEngine";
import { fromLamports } from "@/lib/pumpPaperBondingSim";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { tokenMetaCache } from "@/lib/tokenMetaCache";
import { ExtractAirdropPopover } from "@/components/ExtractAirdropPopover";
import { LongPositionPlugin, LongPositionData } from "@/lib/LongPositionPlugin";
/** Premium Dynamic Crypto Palette */
const BG = "rgba(0, 0, 0, 0)";
const GRID = "rgba(255, 255, 255, 0.03)";
const FG = "rgba(255, 255, 255, 0.9)";
const UP = "#00FFA3"; // Vibrant Neon Green
const DOWN = "#FF3358"; // Vibrant Neon Red
const CROSSHAIR = "rgba(255, 255, 255, 0.4)";
const CROSSHAIR_LABEL_BG = "rgba(15, 20, 25, 0.85)";



/** Rough Solana address shape (base58, typical encode length). */
const LIKELY_MINT_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

function looksLikeSolMint(s: string): boolean {
  return LIKELY_MINT_RE.test(s);
}

function normalizeMint(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const noProto = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");
  const parts = noProto.split(/[\s/?#&]+/).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i];
    if (p && looksLikeSolMint(p)) return p;
  }
  const head = parts[0] ?? trimmed;
  return head.replace(/^[a-z]+:\/\//i, "").split(/[\s/?#]/)[0] ?? "";
}

function snapMarkerToBucket(timeSec: number, bucketSec: number): number {
  if (bucketSec <= 1) return timeSec;
  return Math.floor(timeSec / bucketSec) * bucketSec;
}

/** Map paper trade time to an existing candle `time` so markers attach to a bar. */
function alignPaperMarkerToBars(
  timeSec: number,
  bucketSec: number,
  rows: CandlestickData<UTCTimestamp>[],
): number | null {
  if (rows.length === 0) return null;
  const snap = snapMarkerToBucket(timeSec, bucketSec);
  const times = rows.map((r) => r.time as number);
  if (times.includes(snap)) return snap;
  let best: number | null = null;
  let bestD = Infinity;
  for (const t of times) {
    const d = Math.abs(t - snap);
    if (d < bestD) {
      bestD = d;
      best = t;
    }
  }
  return best;
}

// ── Manual Manage Positions Component ───────────────────────────────────────────

// function ManualManagePositions({
//   onConnectReq,
//   isMobile,
// }: {
//   onConnectReq: () => void;
//   isMobile?: boolean;
// }) {
//   const { connected } = useWallet();
//   const [solBalance, setSolBalance] = useState(125.5);
//   const [assetBalance, setAssetBalance] = useState(45000);
//   const [txState, setTxState] = useState<{
//     type: "buy" | "sell";
//     amount: number | null;
//     status: "idle" | "confirming" | "success";
//   }>({ type: "buy", amount: null, status: "idle" });
//   const [activeSheet, setActiveSheet] = useState<"none" | "buy" | "sell">(
//     "none",
//   );

//   const handleTrade = (type: "buy" | "sell", amount: number) => {
//     if (!connected) {
//       onConnectReq();
//       return;
//     }
//     setTxState({ type, amount, status: "confirming" });
//     setTimeout(() => {
//       if (type === "buy") {
//         setSolBalance((prev) => Math.max(0, prev - amount));
//         setAssetBalance((prev) => prev + amount * 45000); // Mock conversion
//       } else {
//         const sellAmount = (assetBalance * amount) / 100;
//         setAssetBalance((prev) => prev - sellAmount);
//         setSolBalance((prev) => prev + sellAmount / 45000);
//       }
//       setTxState({ type, amount, status: "success" });
//       setTimeout(
//         () => setTxState({ type, amount: null, status: "idle" }),
//         2000,
//       );
//     }, 1500);
//   };

//   if (isMobile) {
//     return (
//       <div className="w-full flex flex-col pointer-events-none">
//         {/* Overlay */}
//         {activeSheet !== "none" && (
//           <div
//             className="fixed inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto transition-opacity z-40"
//             onClick={() => setActiveSheet("none")}
//           />
//         )}

//         {/* Bottom Sheet */}
//         <div
//           className={`fixed bottom-0 left-0 right-0 bg-neutral-900 border-t border-white/10 rounded-t-[2rem] pointer-events-auto transition-transform duration-300 z-50 ${activeSheet !== "none" ? "translate-y-0 shadow-[0_-20px_40px_rgba(0,0,0,0.5)]" : "translate-y-full"}`}
//         >
//           {/* Handle */}
//           <div
//             className="w-full flex justify-center pt-4 pb-2"
//             onClick={() => setActiveSheet("none")}
//           >
//             <div className="w-12 h-1.5 bg-white/20 rounded-full" />
//           </div>

//           <div className="px-5 pb-8 pt-2 flex flex-col gap-5">
//             {/* Balances */}
//             <div className="flex justify-between items-center bg-white/5 rounded-2xl p-4 border border-white/5 shadow-inner">
//               <div className="flex flex-col">
//                 <span className="text-[10px] text-white/50 font-bold uppercase tracking-wider mb-0.5">
//                   Sol Balance
//                 </span>
//                 <span className="text-lg font-black text-white">
//                   {solBalance.toFixed(2)} SOL
//                 </span>
//               </div>
//               <div className="w-px h-10 bg-white/10" />
//               <div className="flex flex-col text-right">
//                 <span className="text-[10px] text-white/50 font-bold uppercase tracking-wider mb-0.5">
//                   Token Value
//                 </span>
//                 <span className="text-lg font-black text-emerald-400">
//                   ${assetBalance.toLocaleString()}
//                 </span>
//               </div>
//             </div>

//             {/* Action Area */}
//             {activeSheet === "buy" && (
//               <div className="flex flex-col gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
//                 <div className="flex items-center gap-2 mb-1">
//                   <TrendingUp className="size-5 text-emerald-400" />
//                   <span className="text-sm font-black text-emerald-400 uppercase tracking-widest">
//                     Quick Buy
//                   </span>
//                 </div>
//                 <div className="grid grid-cols-3 gap-2">
//                   {[0.1, 0.5, 1, 2, 5, 10].map((amount) => (
//                     <button
//                       key={amount}
//                       onClick={() => handleTrade("buy", amount)}
//                       disabled={txState.status !== "idle"}
//                       className="py-3 rounded-xl bg-emerald-500/10 text-emerald-400 font-bold border border-emerald-500/20 hover:bg-emerald-500/20 active:scale-95 transition-all flex items-center justify-center disabled:opacity-50"
//                     >
//                       {txState.status !== "idle" &&
//                       txState.type === "buy" &&
//                       txState.amount === amount ? (
//                         <RefreshCw className="size-4 animate-spin" />
//                       ) : (
//                         `${amount} SOL`
//                       )}
//                     </button>
//                   ))}
//                 </div>
//               </div>
//             )}

//             {activeSheet === "sell" && (
//               <div className="flex flex-col gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
//                 <div className="flex items-center gap-2 mb-1">
//                   <TrendingDown className="size-5 text-rose-400" />
//                   <span className="text-sm font-black text-rose-400 uppercase tracking-widest">
//                     Quick Sell
//                   </span>
//                 </div>
//                 <div className="grid grid-cols-4 gap-2">
//                   {[10, 25, 50, 100].map((pct) => (
//                     <button
//                       key={pct}
//                       onClick={() => handleTrade("sell", pct)}
//                       disabled={txState.status !== "idle"}
//                       className="py-3 rounded-xl bg-rose-500/10 text-rose-400 font-bold border border-rose-500/20 hover:bg-rose-500/20 active:scale-95 transition-all flex items-center justify-center disabled:opacity-50"
//                     >
//                       {txState.status !== "idle" &&
//                       txState.type === "sell" &&
//                       txState.amount === pct ? (
//                         <RefreshCw className="size-4 animate-spin" />
//                       ) : (
//                         `${pct}%`
//                       )}
//                     </button>
//                   ))}
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Persistent Bottom Bar */}
//         <div className="flex gap-4 p-4 pb-8 bg-neutral-900 border-t border-white/10 pointer-events-auto relative z-30 shadow-[0_-10px_20px_rgba(0,0,0,0.3)]">
//           <button
//             onClick={() => setActiveSheet("buy")}
//             className="flex-1 py-4 rounded-2xl bg-emerald-500 font-black text-black text-[15px] uppercase tracking-widest active:scale-95 transition-all shadow-[0_0_30px_rgba(16,185,129,0.3)] flex items-center justify-center gap-2"
//           >
//             <TrendingUp className="size-5" /> Buy
//           </button>
//           <button
//             onClick={() => setActiveSheet("sell")}
//             className="flex-1 py-4 rounded-2xl bg-rose-500 font-black text-white text-[15px] uppercase tracking-widest active:scale-95 transition-all shadow-[0_0_30px_rgba(244,63,94,0.3)] flex items-center justify-center gap-2"
//           >
//             <TrendingDown className="size-5" /> Sell
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="flex items-start justify-center gap-3 pointer-events-none">
//       {/* Buy Section */}
//       <div className="bg-emerald-500/10 backdrop-blur-md rounded-lg p-2.5 border border-emerald-500/20 pointer-events-auto shadow-lg min-w-[240px]">
//         <div className="flex items-center gap-1.5 mb-2">
//           <TrendingUp className="size-3.5 text-emerald-400" />
//           <span className="text-[9px] font-black uppercase tracking-wider text-emerald-400">
//             Quick Buy
//           </span>
//         </div>
//         <div className="flex gap-1 whitespace-nowrap">
//           {[0.1, 0.5, 1, 2, 5].map((amount) => (
//             <button
//               key={amount}
//               onClick={() => handleTrade("buy", amount)}
//               disabled={txState.status !== "idle"}
//               className="flex-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[8px] font-bold border border-emerald-500/20 hover:bg-emerald-500/20 transition-all disabled:opacity-50"
//             >
//               {txState.status !== "idle" &&
//               txState.type === "buy" &&
//               txState.amount === amount
//                 ? "..."
//                 : `${amount} SOL`}
//             </button>
//           ))}
//         </div>
//         <div className="flex gap-1 mt-1.5">
//           {[10, 25, 50, 100].map((pct) => (
//             <button
//               key={pct}
//               className="flex-1 px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-[8px] font-bold border border-emerald-500/30 hover:bg-emerald-500/30 transition-all"
//             >
//               {pct}%
//             </button>
//           ))}
//         </div>
//       </div>

//       {/* Balances Center */}
//       <div className="flex flex-col items-center justify-center pointer-events-auto bg-black/40 backdrop-blur-md px-4 py-2 rounded-xl border border-white/10 shadow-lg min-w-max">
//         <span className="text-[9px] font-black uppercase tracking-widest text-white/60 mb-1 whitespace-nowrap">
//           Manual Control
//         </span>
//         <div className="flex items-center gap-1.5 whitespace-nowrap">
//           <span className="text-xs font-bold text-white">
//             {solBalance.toFixed(2)} SOL
//           </span>
//           <span className="text-white/30">•</span>
//           <span className="text-xs font-bold text-emerald-400">
//             ${assetBalance.toLocaleString()}
//           </span>
//         </div>
//         <button className="mt-2 px-3 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-[9px] font-bold border border-emerald-500/20 hover:bg-emerald-500/20 transition-all w-full">
//           Refresh
//         </button>
//       </div>

//       {/* Sell Section */}
//       <div className="bg-rose-500/10 backdrop-blur-md rounded-lg p-2.5 border border-rose-500/20 pointer-events-auto shadow-lg min-w-[240px]">
//         <div className="flex items-center gap-1.5 mb-2">
//           <TrendingDown className="size-3.5 text-rose-400" />
//           <span className="text-[9px] font-black uppercase tracking-wider text-rose-400">
//             Quick Sell
//           </span>
//         </div>
//         <div className="flex gap-1 whitespace-nowrap">
//           {[0.1, 0.5, 1, 2, 5].map((amount) => (
//             <button
//               key={amount}
//               className="flex-1 px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 text-[8px] font-bold border border-rose-500/20 hover:bg-rose-500/20 transition-all"
//             >
//               {amount} SOL
//             </button>
//           ))}
//         </div>
//         <div className="flex gap-1 mt-1.5">
//           {[10, 25, 50, 100].map((pct) => (
//             <button
//               key={pct}
//               onClick={() => handleTrade("sell", pct)}
//               disabled={txState.status !== "idle"}
//               className="flex-1 px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 text-[8px] font-bold border border-rose-500/30 hover:bg-rose-500/30 transition-all disabled:opacity-50"
//             >
//               {txState.status !== "idle" &&
//               txState.type === "sell" &&
//               txState.amount === pct
//                 ? "..."
//                 : `${pct}%`}
//             </button>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// }

export function CaChartPanel({
  mint: propMint,
  onTitleClick,
  isFocus = false,
}: {
  mint?: string;
  onTitleClick?: () => void;
  isFocus?: boolean;
}) {
  const { t } = useTranslation();
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);

  // Manual trading & Limit orders states moved to LiquidityPoolToolbar
  const {
    caMintInput: globalMint,
    selectedAlgoId,
    setChartAnalytics,
    algoSessionActive,
    tradingMode,
    tradingHalted,
    hardStopTrading,
    scalperLiveBuySol,
    appendPersistedTrades,
    bounceZones,
    setDetectedZones,
    updateBounceZonePrice,
    scalperUserConfig,
    bounceSuggestionTick,
    setFloorCandlesStatus,
    model,
    manualSellRequested,
    clearManualSellRequest,
    theme,
    setIsTrading,
    solPrice,
    heliusKey,
    locale,

  } = useApp() as any;

  const { serverOpenPositions } = useSocket();

  const { connected, secretKey, publicKey } = useWallet();

  const isMobile = useIsMobile();
  const caMintInput = propMint || globalMint;

  const streamMint = useMemo(() => {
    const m = normalizeMint(propMint || caMintInput);
    return looksLikeSolMint(m) ? m : null;
  }, [propMint, caMintInput]);

  const [debouncedMint, setDebouncedMint] = useState("");
  const activeMint = streamMint || debouncedMint;
  const isSyntheticRef = useRef(false);

  // Zoom HUD state
  const [zoomStats, setZoomStats] = useState({
    barSpacing: 6,
    rightOffset: 12,
  });
  


  const hostRef = useRef<HTMLDivElement | null>(null);
  const fvgContainerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const svixSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const seriesMarkersPluginRef = useRef<ISeriesMarkersPluginApi<any> | null>(
    null,
  );
  const longPositionPluginRef = useRef<LongPositionPlugin | null>(null);
  /** Zone id → chart price line (updated during drag without React thrash). */
  const bouncePriceLineHandlesRef = useRef<Map<string, BouncePriceLineHandle>>(
    new Map(),
  );

  const fetchGenRef = useRef(0);
  const didFitRef = useRef(false);
  /**
   * Ref-stable snapshot of baseRows used to detect full-refresh vs live-tick in the chart
   * data effect. When only live rows change we use `series.update()` which preserves price
   * lines; when baseRows change we need `setData()` and must re-draw bounce lines after.
   */
  /** Holds the handles for the server paper trade entry and exit price lines. */
  const serverPaperTradeLinesRef = useRef<BouncePriceLineHandle[]>([]);
  const prevBaseRowsRef = useRef<typeof baseRows>([]);
  const lastRenderedMintRef = useRef<string | null>(null);

  const [serverPastTrades, setServerPastTrades] = useState<any[]>([]);

  useEffect(() => {
    if (!activeMint) {
      setServerPastTrades([]);
      return;
    }
    fetch(`/api/server-paper-trades?mint=${activeMint}&limit=20`)
      .then(res => res.json())
      .then(data => {
        const trades = Array.isArray(data) ? data : (data?.trades || []);
        setServerPastTrades(trades);
      })
      .catch(() => {});
  }, [activeMint]);

  const [chartInterval, setChartInterval] = useState<ChartInterval>("1s");
  const [chartTzChoice] = useState<ChartTimezoneChoice>(() =>
    loadChartTimezoneChoice(),
  );
  const [scalperCutoffMs, setScalperCutoffMs] = useState<number | null>(null);
  const [livePumpPortalSig, setLivePumpPortalSig] = useState<string | null>(
    null,
  );
  const [livePumpPortalErr, setLivePumpPortalErr] = useState<string | null>(
    null,
  );
  const [localHeight, setLocalHeight] = useState<number | "">("");
  /** Confirmed Lightning round-trips with RPC-parsed wallet SOL (real mode only). */
  const [liveChainTrades, setLiveChainTrades] = useState<BotTradeRowChain[]>(
    [],
  );
  const [chartSessionNotice, setChartSessionNotice] = useState<string | null>(
    null,
  );



  // HTML Overlay state for profile images
  const markersContainerRef = useRef<HTMLDivElement | null>(null);
  const [renderableGroups, setRenderableGroups] = useState<any[]>([]);

  // Real-time synchronization loop to pin HTML elements to the chart canvas coordinates
  const updateMarkerPositions = useCallback(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    const container = markersContainerRef.current;
    if (!chart || !series || !container) return;

    const ts = chart.timeScale();
    const nodes = container.children;

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i] as HTMLElement;
      const timeStr = node.getAttribute("data-time");
      const priceStr = node.getAttribute("data-price");
      const isBuyStr = node.getAttribute("data-isbuy");

      if (!timeStr || !priceStr) continue;

      const time = parseFloat(timeStr);
      const price = parseFloat(priceStr);
      const isBuy = isBuyStr === "true";

      const x = ts.timeToCoordinate(time as UTCTimestamp);
      const y = series.priceToCoordinate(price);

      // Hide if out of bounds or calculation fails
      console.log("[DEBUG MARKERS]", { time, price, x, y });
      if (x === null || y === null) {
        node.style.opacity = "0";
        continue;
      }

      // Offset pushes buys below the wick, and sells above the wick
      // 35px offset accommodates the stem and the bubble
      const offset = isBuy ? 35 : -35;
      node.style.opacity = "1";
      node.style.transform = `translate(calc(${x}px - 50%), calc(${y + offset}px - 50%))`;
    }
  }, []);

  // Sync positions when the DOM groups physically update
  useEffect(() => {
    requestAnimationFrame(updateMarkerPositions);
  }, [renderableGroups, updateMarkerPositions]);

  // Trader profile tooltip state
  const [tooltipMarker, setTooltipMarker] = useState<{
    marker: (typeof walletExecutionMarkers)[number];
    position: { x: number; y: number };
  } | null>(null);

  useEffect(() => {
    if (!chartSessionNotice) return;
    const tick = window.setTimeout(() => setChartSessionNotice(null), 6200);
    return () => window.clearTimeout(tick);
  }, [chartSessionNotice]);

  const effectiveTz = useMemo(
    () => effectiveChartTimeZone(chartTzChoice),
    [chartTzChoice],
  );


  const [mintLoaded, setMintLoaded] = useState<string | null>(null);
  const [baseRows, setBaseRows] = useState<CandlestickData<UTCTimestamp>[]>([]);
  const [yMcCap, setYMcCap] = useState(true);
  /** SPL minted supply (`getTokenSupply` uiAmount) when RPC succeeded. */
  const [tokenUiSupply, setTokenUiSupply] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prevActiveMint, setPrevActiveMint] = useState<string | null>(null);

  // Synchronous Frame-0 State Adjustment (0ms switch, zero blank screen flash)
  if (activeMint !== prevActiveMint) {
    setPrevActiveMint(activeMint);
    if (!activeMint) {
      setMintLoaded(null);
      setBaseRows([]);
      setTokenUiSupply(null);
      setError(null);
      setLoading(false);
    } else {
      const cached = getCachedCandles(activeMint, chartInterval);
      if (cached && cached.candles.length > 0) {
        setMintLoaded(activeMint);
        setYMcCap(cached.yAxisIsMarketCapUsd);
        if (cached.tokenUiSupply != null) setTokenUiSupply(cached.tokenUiSupply);
        setBaseRows(cached.candles);
        setLoading(false);
        setError(null);
      } else {
        const activePos = serverOpenPositions?.find((p: any) => p.mint === activeMint);
        const pastTrade = serverPastTrades?.find((p: any) => p.mint === activeMint);
        const pos = activePos || pastTrade;
        if (pos) {
          const entryMc = pos.entryMcUsd || (pos.entryPrice ? pos.entryPrice * 1e9 : null);
          const exitOrCurrentMc = pos.currentMcUsd || pos.exitMcUsd || entryMc;
          const highMc = pos.highestMcUsd || Math.max(entryMc || 0, exitOrCurrentMc || 0);
          const lowMc = pos.lowestMcUsd || Math.min(entryMc || Infinity, exitOrCurrentMc || Infinity);
          const entrySec = pos.entryTime ? Math.floor(pos.entryTime / 1000) : (Math.floor(Date.now() / 1000) - 60);
          const nowSec = pos.timestamp ? Math.floor(pos.timestamp / 1000) : Math.floor(Date.now() / 1000);
          
          if (entryMc && Number.isFinite(entryMc)) {
            const seedCandles: CandlestickData<UTCTimestamp>[] = [
              {
                time: entrySec as UTCTimestamp,
                open: entryMc,
                high: highMc && Number.isFinite(highMc) ? highMc : entryMc,
                low: lowMc && Number.isFinite(lowMc) ? lowMc : entryMc,
                close: exitOrCurrentMc && Number.isFinite(exitOrCurrentMc) ? exitOrCurrentMc : entryMc,
              },
              {
                time: Math.max(entrySec + 1, nowSec) as UTCTimestamp,
                open: entryMc,
                high: highMc && Number.isFinite(highMc) ? highMc : entryMc,
                low: lowMc && Number.isFinite(lowMc) ? lowMc : entryMc,
                close: exitOrCurrentMc && Number.isFinite(exitOrCurrentMc) ? exitOrCurrentMc : entryMc,
              }
            ];
            isSyntheticRef.current = true;
            setMintLoaded(activeMint);
            setYMcCap(true);
            setBaseRows(seedCandles);
            setLoading(false);
          } else {
            setMintLoaded(activeMint);
            setBaseRows([]);
            setLoading(true);
          }
        } else {
          const nToken = nursery.getNurseryToken(activeMint);
          const meta = tokenMetaCache.get(activeMint);
          const effSolPrice = Number(solPrice) > 0 ? Number(solPrice) : 150;

          let curMc = 0;
          let peakMc = 0;
          let initMc = 0;
          let firstSec = Math.floor(Date.now() / 1000) - 120;
          let lastSec = Math.floor(Date.now() / 1000);

          if (nToken) {
            curMc = (nToken.marketCapSol || 0) * effSolPrice;
            peakMc = (nToken.peakMcSol || nToken.marketCapSol || 0) * effSolPrice;
            initMc = 30 * effSolPrice;
            if (nToken.firstSeenMs) firstSec = Math.floor(nToken.firstSeenMs / 1000);
            if (nToken.lastTradeMs) lastSec = Math.floor(nToken.lastTradeMs / 1000);
          } else if (meta) {
            curMc = (meta as any).usd_market_cap || (((meta as any).market_cap || 0) * effSolPrice);
            peakMc = curMc * 1.1;
            initMc = curMc * 0.9;
          }

          if (curMc > 0 && Number.isFinite(curMc)) {
            const seedCandles: CandlestickData<UTCTimestamp>[] = [
              {
                time: Math.min(firstSec, lastSec - 1) as UTCTimestamp,
                open: initMc > 0 ? initMc : curMc,
                high: Math.max(peakMc, curMc, initMc || curMc),
                low: Math.min(initMc > 0 ? initMc : curMc, curMc),
                close: curMc,
              },
              {
                time: Math.max(firstSec + 1, lastSec) as UTCTimestamp,
                open: initMc > 0 ? initMc : curMc,
                high: Math.max(peakMc, curMc),
                low: Math.min(initMc > 0 ? initMc : curMc, curMc),
                close: curMc,
              }
            ];
            isSyntheticRef.current = true;
            setMintLoaded(activeMint);
            setYMcCap(true);
            setBaseRows(seedCandles);
            setLoading(false);
          } else {
            setMintLoaded(activeMint);
            setBaseRows([]);
            setLoading(true);
          }
        }
      }
    }
  }
  const [showFvg, setShowFvg] = useState(true);
  /**
   * 3 × 1000 5m candles prefetched when a mint loads — used by "Suggest lines" on demand.
   * Always 5m so the detector sees ~10 days of structural history regardless of chart interval.
   */
  const [floorDetectionRows, setFloorDetectionRows] = useState<
    CandlestickData<UTCTimestamp>[]
  >([]);
  const floorFetchGenRef = useRef(0);


  // Safety watchdog: ensure loading never hangs indefinitely
  useEffect(() => {
    if (!loading) return;
    const t = window.setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => window.clearTimeout(t);
  }, [loading]);



  const {
    rows: liveRows,
    state: liveState,
    error: liveErr,
    lastTradeAt: liveLastTradeAt,
    stats: liveStats,
  } = usePumpPortalTrades(streamMint, 200);

  // Wallet execution markers - show trader profiles on chart
  const { markers: walletExecutionMarkers } = useWalletExecutionMarkers(
    streamMint,
    100,
    true,
  );



  const bucketSec = chartIntervalBucketSec(chartInterval);

  /** PumpPortal MC often matches price × 1e9; multiply by minted/1e9 to match on-chain MC. */
  const portalMcFactor = useMemo(
    () => (yMcCap ? pumpPortalMcScaleFactor(tokenUiSupply) : 1),
    [yMcCap, tokenUiSupply],
  );

  const liveRowsForMc = useMemo(() => {
    if (portalMcFactor === 1) return liveRows;
    return liveRows.map((r) =>
      r.mcUsd == null ? r : { ...r, mcUsd: r.mcUsd * portalMcFactor },
    );
  }, [liveRows, portalMcFactor]);

  /** Lightning pool hint from tape (bonding snapshots vs graduated prints). */
  const lightningPoolPreference = useMemo(() => {
    const cfg = SCALPER_PAPER_CONFIG.realPool as string;
    if (cfg !== "auto") return cfg;
    const h = inferPumpPortalTradePool(liveRowsForMc);
    return h === "auto" ? "auto" : h;
  }, [liveRowsForMc]);

  const chartRows = useMemo(
    () => mergeLiveMcUsdIntoCandles(baseRows, liveRowsForMc, bucketSec),
    [baseRows, liveRowsForMc, bucketSec],
  );

  // Whenever chart already has candles rendered, dismiss loading overlay immediately
  useEffect(() => {
    if (chartRows.length > 0 && loading) {
      setLoading(false);
    }
  }, [chartRows.length, loading]);

  // FVG Detection
  const fvgs = useMemo(() => {
    if (!showFvg || chartRows.length < 3) return [];
    
    type FvgZone = {
      id: string;
      type: 'bullish' | 'bearish';
      top: number;
      bottom: number;
      startTime: UTCTimestamp;
      endTime?: UTCTimestamp;
    };
    
    const gaps: FvgZone[] = [];
    
    for (let i = 2; i < chartRows.length; i++) {
      const c1 = chartRows[i - 2];
      const c2 = chartRows[i - 1];
      const c3 = chartRows[i];
      
      for (const gap of gaps) {
        if (gap.endTime) continue;
        // completely filled mitigation logic
        if (gap.type === 'bullish' && c3.low <= gap.bottom) {
          gap.endTime = c3.time as UTCTimestamp;
        } else if (gap.type === 'bearish' && c3.high >= gap.top) {
          gap.endTime = c3.time as UTCTimestamp;
        }
      }
      
      if (c3.low > c1.high) {
        gaps.push({
          id: `fvg-bull-${c2.time}`,
          type: 'bullish',
          top: c3.low,
          bottom: c1.high,
          startTime: c2.time as UTCTimestamp,
        });
      } else if (c3.high < c1.low) {
        gaps.push({
          id: `fvg-bear-${c2.time}`,
          type: 'bearish',
          top: c1.low,
          bottom: c3.high,
          startTime: c2.time as UTCTimestamp,
        });
      }
    }
    
    return gaps.filter(g => !g.endTime);
  }, [chartRows, showFvg]);

  const svix = useSvixEngine(liveRows);

  useEffect(() => {
    if (svix && svixSeriesRef.current && chartRows.length > 0) {
      // align SVIX to the current bar time
      const lastBar = chartRows[chartRows.length - 1];
      const svixTime = Math.max(lastBar.time as number, svix.timeSec);
      svixSeriesRef.current.update({
        time: svixTime as UTCTimestamp,
        value: svix.svix,
        color: svix.svix > 60 ? 'rgba(239, 68, 68, 0.4)' : 'rgba(59, 130, 246, 0.4)'
      });
    }
  }, [svix, chartRows]);

  const lastCandleSummary = useMemo((): ChartCandleBarSummary | null => {
    if (chartRows.length === 0) return null;
    const last = chartRows[chartRows.length - 1]!;
    return {
      interval: chartInterval,
      timeUnix: last.time as number,
      open: last.open,
      high: last.high,
      low: last.low,
      close: last.close,
      yAxisIsMarketCapUsd: yMcCap,
    };
  }, [chartRows, chartInterval, yMcCap]);

  const tapeSummary = useMemo((): ChartTapeSummary | null => {
    const active = mintLoaded ?? streamMint;
    if (!active) return null;
    if (liveRowsForMc.length === 0) {
      return {
        sampleSize: 0,
        buyCount: 0,
        sellCount: 0,
        solVolume: 0,
        latestMcUsd: null,
        latestLiqSol: null,
        buyPct: 50,
        recentPrints: [],
      };
    }
    const sample = liveRowsForMc;
    let buyCount = 0;
    let sellCount = 0;
    let solVolume = 0;
    for (const r of sample) {
      if (r.buy) buyCount++;
      else sellCount++;
      solVolume += r.sol;
    }
    let latestMcUsd: number | null = sample[0]?.mcUsd ?? null;
    if (latestMcUsd == null) {
      for (const r of sample) {
        if (r.mcUsd != null) {
          latestMcUsd = r.mcUsd;
          break;
        }
      }
    }

    let latestLiqSol: number | null = null;
    for (const r of sample) {
      if (r.bonding?.virtualSolLamports) {
        latestLiqSol = fromLamports(r.bonding.virtualSolLamports);
        break;
      }
    }

    const buyPct =
      sample.length > 0 ? Math.round((buyCount / sample.length) * 100) : 50;

    const recentPrints = sample.slice(0, 15).map((r) => ({
      ts: r.ts,
      buy: r.buy,
      sol: r.sol,
      mcUsd: r.mcUsd,
    }));

    return {
      sampleSize: sample.length,
      buyCount,
      sellCount,
      solVolume,
      latestMcUsd,
      latestLiqSol,
      buyPct,
      recentPrints,
    };
  }, [mintLoaded, streamMint, liveRowsForMc]);

  const scalperEngineRunning =
    algoSessionActive &&
    (selectedAlgoId === BUILTIN_SCALPER_PRESET_ID ||
      selectedAlgoId === BUILTIN_REVIVAL_HUNTER_PRESET_ID) &&
    (tradingMode === "paper" || tradingMode === "real");

  const prevLiveOpenRef = useRef<boolean | undefined>(undefined);
  const lastLiveTxAtRef = useRef(0);
  const pendingLiveBuySigRef = useRef<string | null>(null);
  /** Venue used for the confirmed Lightning buy — sells reuse it so post-migration tokens exit on AMM. */
  const lastBuyVenueRef = useRef<{ mint: string; pool: string } | null>(null);
  /**
   * True only after a real Lightning buy is confirmed on-chain.
   * Guards sells so we never attempt to sell tokens we never actually received
   * (e.g. when the buy returned 400 but prevLiveOpenRef already flipped to true).
   */
  const realPositionOpenRef = useRef(false);

  useEffect(() => {
    const running =
      algoSessionActive &&
      selectedAlgoId === BUILTIN_SCALPER_PRESET_ID &&
      !!mintLoaded &&
      (tradingMode === "paper" || tradingMode === "real");
    if (!running) {
      setScalperCutoffMs(null);
      return;
    }
    setScalperCutoffMs(Date.now());
    if (tradingMode === "real") {
      setLivePumpPortalSig(null);
      setLivePumpPortalErr(null);
      // Reset pending buy ref so a stale buy sig from the previous session
      // can't be paired with a sell from a new session.
      pendingLiveBuySigRef.current = null;
      lastBuyVenueRef.current = null;
      realPositionOpenRef.current = false;
    }
  }, [algoSessionActive, selectedAlgoId, mintLoaded, tradingMode]);

  useEffect(() => {
    if (tradingMode !== "real") {
      setLivePumpPortalSig(null);
      setLivePumpPortalErr(null);
      pendingLiveBuySigRef.current = null;
      lastBuyVenueRef.current = null;
      realPositionOpenRef.current = false;
    }
  }, [tradingMode]);

  // --- FVG Sync logic ---
  useEffect(() => {
    if (!showFvg || !chartRef.current || !seriesRef.current || !fvgContainerRef.current) return;
    
    let rafId: number;
    const syncCoords = () => {
      const chart = chartRef.current;
      const series = seriesRef.current;
      const container = fvgContainerRef.current;
      if (!chart || !series || !container) return;
      
      const children = container.children;
      for (let i = 0; i < children.length; i++) {
        const el = children[i] as HTMLElement;
        const startTs = Number(el.dataset.start);
        const endTs = el.dataset.end ? Number(el.dataset.end) : null;
        const topPrice = Number(el.dataset.top);
        const bottomPrice = Number(el.dataset.bottom);
        
        let startX = chart.timeScale().timeToCoordinate(startTs as Time);
        let endX = endTs ? chart.timeScale().timeToCoordinate(endTs as Time) : chart.timeScale().width();
        
        if (startX === null && endX !== null) {
          startX = -1000 as any;
        }
        
        const topY = series.priceToCoordinate(topPrice);
        const bottomY = series.priceToCoordinate(bottomPrice);
        
        if (startX !== null && endX !== null && topY !== null && bottomY !== null) {
          el.style.left = `${Math.min(startX, endX)}px`;
          el.style.width = `${Math.max(0, Math.abs(endX - startX))}px`;
          el.style.top = `${Math.min(topY, bottomY)}px`;
          el.style.height = `${Math.max(0, Math.abs(bottomY - topY))}px`;
          el.style.display = 'block';
        } else {
          el.style.display = 'none';
        }
      }
      rafId = requestAnimationFrame(syncCoords);
    };
    
    rafId = requestAnimationFrame(syncCoords);
    return () => cancelAnimationFrame(rafId);
  }, [showFvg, fvgs]);

  // --- Core Lifecycle ---
  useEffect(() => {
    if (tradingMode !== "real") return;
    pendingLiveBuySigRef.current = null;
    lastBuyVenueRef.current = null;
    realPositionOpenRef.current = false;
  }, [mintLoaded, tradingMode]);

  // ── Manual "Sell All" — fires an immediate 100 % sell regardless of engine state ──
  useEffect(() => {
    if (!manualSellRequested) return;
    if (tradingMode !== "real" || !mintLoaded || !realPositionOpenRef.current) {
      clearManualSellRequest();
      hardStopTrading();
      return;
    }

    clearManualSellRequest();
    realPositionOpenRef.current = false;

    if (!connected || !secretKey || !publicKey || !heliusKey) {
      setLivePumpPortalErr(
        appendPumpPortalTradingWalletHint(
          "Internal Wallet or Helius RPC Key missing.",
        ),
      );
      hardStopTrading();
      return;
    }

    const pool =
      lastBuyVenueRef.current?.mint === mintLoaded &&
      lastBuyVenueRef.current.pool
        ? lastBuyVenueRef.current.pool
        : lightningPoolPreference;

    const buySig = pendingLiveBuySigRef.current;
    pendingLiveBuySigRef.current = null;
    lastBuyVenueRef.current = null;

    void (async () => {
      const res = await postPumpPortalLocalTradeWithFallback(secretKey, heliusKey, {
        publicKey,
        action: "sell",
        mint: mintLoaded,
        amount: "100%",
        denominatedInSol: "false",
        slippage: scalperUserConfig.realSlippagePct,
        priorityFee: scalperUserConfig.realPriorityFeeSol,
        pool,
      });

      if (!res.ok) {
        const tried =
          res.attempts.length > 1
            ? ` — venues tried: ${res.attempts.join(" → ")}`
            : "";
        setLivePumpPortalErr(
          appendPumpPortalTradingWalletHint(
            `Manual sell failed: ${res.message}${tried}`,
          ),
        );
        hardStopTrading();
        return;
      }

      setLivePumpPortalSig(`MANUAL SELL confirming… ${res.signature}`);
      const { confirmed } = await confirmLightningTx(res.signature);
      if (!confirmed) {
        setLivePumpPortalErr(
          appendPumpPortalTradingWalletHint(
            "Manual sell did not confirm — check Solscan.",
          ),
        );
        hardStopTrading();
        return;
      }

      setLivePumpPortalSig(res.signature);

      const walletPk = getPumpPortalTradingWalletPubkey();
      if (buySig && walletPk) {
        const buyDelta = await fetchWalletSolDeltaSol(buySig, walletPk);
        const sellDelta = await fetchWalletSolDeltaSol(res.signature, walletPk);
        if (buyDelta != null && sellDelta != null) {
          const solSpent = Math.max(0, -buyDelta);
          const solReceived = Math.max(0, sellDelta);
          const netSol = buyDelta + sellDelta;
          const roiPct = solSpent > 0 ? (netSol / solSpent) * 100 : 0;
          const chainTrade: BotTradeRowChain = {
            kind: "chain",
            id: `chain-manual-${res.signature}`,
            entryTs: Date.now(),
            closedAtTs: Date.now(),
            exitReason: "order_book_sell",
            buySignature: buySig,
            sellSignature: res.signature,
            solSpent,
            solReceived,
            netSol,
            roiPct,
          };
          setLiveChainTrades((prev) => [...prev, chainTrade]);
          const meta = tokenMetaCache.get(mintLoaded);
          appendPersistedTrades([
            { 
              ...chainTrade, 
              walletPk, 
              mint: mintLoaded,
              symbol: meta?.symbol,
              imageUri: meta?.imageUri,
            },
          ]);
        }
      }

      hardStopTrading();
    })();
  }, [
    manualSellRequested,
    clearManualSellRequest,
    tradingMode,
    mintLoaded,
    hardStopTrading,
    lightningPoolPreference,
    scalperUserConfig.realSlippagePct,
    scalperUserConfig.realPriorityFeeSol,
    appendPersistedTrades,
  ]);

  const mintZones = useMemo(
    () =>
      mintLoaded
        ? bounceZones.filter((z: UserBounceZone) => z.mint === mintLoaded)
        : [],
    [bounceZones, mintLoaded],
  );
  /**
   * `undefined` — no bounce rows for this mint → scalper uses dip+catalyst only.
   * `[]` — rows exist but all disabled → proximity filter never passes → no buys.
   * `[prices]` — MC must be within ±10% of an enabled zone to arm.
   */
  const activeBounceZonePricesForScalper = useMemo(() => {
    if (!mintLoaded || mintZones.length === 0) return undefined;
    return mintZones
      .filter((z: UserBounceZone) => z.enabled)
      .map((z: UserBounceZone) => z.price);
  }, [mintLoaded, mintZones]);

  const paperScalper = useMemo(() => {
    if (!algoSessionActive || !mintLoaded || scalperCutoffMs == null)
      return null;

    if (selectedAlgoId === BUILTIN_SCALPER_PRESET_ID) {
      return reduceScalperPaper(liveRowsForMc, {
        minTradeTsMs: scalperCutoffMs,
        paperBuySol: tradingMode === "paper" ? scalperLiveBuySol : undefined,
        activeBounceZonePrices: activeBounceZonePricesForScalper,
        scalperConfig: scalperUserConfig,
      });
    }

    if (selectedAlgoId === BUILTIN_REVIVAL_HUNTER_PRESET_ID) {
      const snap = nursery.getSnapshot();
      const allTokens = snap.visibleTokens || [];
      const token = allTokens.find((t) => t.mint === mintLoaded);
      const revivalScore = (token as any)?.revivalScore ?? 0;
      const liquidityUsd =
        (token as any)?.marketCapUsd ?? (token as any)?.marketCapSol * 25 ?? 0; // fallback scaling

      return reduceRevivalHunter(liveRowsForMc, revivalScore, liquidityUsd, {
        scalperConfig: scalperUserConfig,
      }) as any;
    }

    return null;
  }, [
    algoSessionActive,
    selectedAlgoId,
    mintLoaded,
    liveRowsForMc,
    scalperCutoffMs,
    tradingMode,
    scalperLiveBuySol,
    activeBounceZonePricesForScalper,
    scalperUserConfig,
  ]);

  const lastPersistedPaperCountRef = useRef(0);

  // Drag refs — kept stable so event listeners don't need re-adding on every render
  const draggingZoneIdRef = useRef<string | null>(null);
  const dragPointerIdRef = useRef<number | null>(null);
  const pendingDragPriceRef = useRef<number | null>(null);
  const bounceZonesRef = useRef(bounceZones);
  const mintLoadedRef = useRef(mintLoaded);
  const updateBounceZonePriceRef = useRef(updateBounceZonePrice);
  useEffect(() => {
    bounceZonesRef.current = bounceZones;
  }, [bounceZones]);
  useEffect(() => {
    mintLoadedRef.current = mintLoaded;
  }, [mintLoaded]);
  useEffect(() => {
    updateBounceZonePriceRef.current = updateBounceZonePrice;
  }, [updateBounceZonePrice]);

  /** Live last-close price — kept current via ref so drag/add callbacks always have it. */
  const livePriceRef = useRef<number | null>(null);
  useEffect(() => {
    if (lastCandleSummary) livePriceRef.current = lastCandleSummary.close;
  }, [lastCandleSummary]);

  // ── Lazy historical load (pan-left → fetch older chunks) ──────────────────
  /** Oldest bar timestamp (seconds) in baseRows — cursor for the next older-chunk fetch. */
  const oldestLoadedTsRef = useRef<number | null>(null);
  /** Guard: true while an older-history chunk request is in-flight. */
  const isFetchingOlderRef = useRef(false);
  /** True once a chunk returns fewer bars than the limit — we've reached token genesis. */
  const hasReachedGenesisRef = useRef(false);
  /** Ref mirror of chartInterval for use inside the timeScale subscription closure. */
  const chartIntervalRef = useRef(chartInterval);
  useEffect(() => {
    chartIntervalRef.current = chartInterval;
  }, [chartInterval]);

  // Update oldest-ts cursor whenever baseRows grows (initial load or chunk prepend).
  useEffect(() => {
    if (baseRows.length > 0) {
      oldestLoadedTsRef.current = baseRows[0]!.time as number;
    }
  }, [baseRows]);

  /**
   * Re-draw all bounce zone price lines after `series.setData()` wipes them.
   * Reads from refs so it can be called inside any effect without adding deps.
   */
  /** Merge zones whose prices almost coincide — prevents stacked duplicate axis labels. */
  const dedupeZonesForDraw = (zones: UserBounceZone[]): UserBounceZone[] => {
    // Two zones in the same 15% price band → keep only the stronger one.
    const RANGE_FRAC = 0.15;
    // Absolute maximum lines ever drawn on the chart.
    const MAX_DRAW = 3;

    if (zones.length === 0) return [];

    // Step 1: sort by strength descending so the strongest zone in each band wins.
    const byStrength = [...zones].sort(
      (a, b) => (b.strength ?? 0) - (a.strength ?? 0),
    );

    // Step 2: greedy keep — add a zone only if no already-kept zone is within RANGE_FRAC.
    const kept: UserBounceZone[] = [];
    for (const z of byStrength) {
      if (z.price <= 0) continue;
      const tooClose = kept.some(
        (k) =>
          Math.abs(k.price - z.price) / Math.min(k.price, z.price) < RANGE_FRAC,
      );
      if (!tooClose) kept.push({ ...z });
      if (kept.length >= MAX_DRAW) break;
    }

    return kept;
  };

  const redrawBounceLinesOnSeries = (s: ISeriesApi<"Candlestick">) => {
    const mint = mintLoadedRef.current;
    if (!mint) return;
    const raw = bounceZonesRef.current.filter(
      (z: UserBounceZone) => z.mint === mint && z.enabled,
    );
    const zones = dedupeZonesForDraw(raw);
    bouncePriceLineHandlesRef.current.clear();
    const si = s as unknown as { _bounceLines?: BouncePriceLineHandle[] };
    if (si._bounceLines) {
      for (const line of si._bounceLines) {
        try {
          s.removePriceLine(line);
        } catch {
          /* stale handle after setData — ignore */
        }
      }
    }
    si._bounceLines = [];
    // Track prices already drawn this pass — skip any that land within 15% of an existing line.
    const drawnPrices: number[] = [];
    for (const zone of zones) {
      if (zone.price <= 0) continue;
      // Hard check: never draw two lines within 15% of each other no matter what
      const tooClose = drawnPrices.some(
        (p) => Math.abs(p - zone.price) / Math.min(p, zone.price) < 0.15,
      );
      if (tooClose) continue;
      drawnPrices.push(zone.price);

      const sources = (zone as { sources?: string }).sources;
      const isAuto = sources != null;
      const opacity = Math.max(0.45, zone.strength ?? 0.55);
      const tfCount = isAuto ? sources!.split("+").length : 1;
      const title = isAuto
        ? sources === "swing"
          ? "Swing low"
          : sources!.startsWith("swing+")
            ? `Swing · ${sources!.slice("swing+".length)}`
            : sources!
        : "zone";
      const isEmerald = title.toLowerCase().includes("vision") || title.toLowerCase().includes("swing");
      const color = isEmerald ? `rgba(16, 185, 129, ${opacity})` : `rgba(56,189,248,${opacity})`;
      const line = s.createPriceLine({
        price: zone.price,
        color,
        lineWidth: tfCount >= 3 ? 2 : 1,
        lineStyle: isAuto ? 0 : 2,
        axisLabelVisible: true,
        title,
      });
      si._bounceLines.push(line);
      bouncePriceLineHandlesRef.current.set(zone.id, line);
    }
  };

  // Persist newly closed paper trades as they accumulate in the session.
  useEffect(() => {
    if (!paperScalper || !mintLoaded) return;
    const all = paperScalper.botTrades;
    const newCount = all.length;
    if (newCount <= lastPersistedPaperCountRef.current) return;
    const fresh = all.slice(lastPersistedPaperCountRef.current);
    lastPersistedPaperCountRef.current = newCount;
    const walletPk = getPumpPortalTradingWalletPubkey() ?? "paper";
    const meta = tokenMetaCache.get(mintLoaded);
    appendPersistedTrades(
      fresh.map((t: any) => ({ 
        ...t, 
        walletPk, 
        mint: mintLoaded,
        symbol: meta?.symbol,
        imageUri: meta?.imageUri,
      })),
    );
  }, [paperScalper, mintLoaded, appendPersistedTrades]);

  // Reset paper persist counter when a new session starts or mint changes.
  useEffect(() => {
    lastPersistedPaperCountRef.current = 0;
  }, [scalperCutoffMs, mintLoaded]);

  useEffect(() => {
    if (tradingMode !== "real" || !algoSessionActive || tradingHalted) {
      prevLiveOpenRef.current = undefined;
      return;
    }
    if (!mintLoaded || liveState !== "open" || !paperScalper) return;

    const apiKey = getEffectivePumpPortalApiKey().trim();
    const open = paperScalper.currentTrade != null;
    const prev = prevLiveOpenRef.current;

    if (prev === undefined) {
      prevLiveOpenRef.current = open;
      return;
    }

    const now = Date.now();
    const minGap = 1500;

    const maybeBuy = !prev && open;
    // Only attempt a real sell if a real buy was actually confirmed on-chain.
    // Without this guard, a failed buy (400) would still flip prevLiveOpenRef=true
    // and trigger a phantom sell on the next exit signal → "could not find account".
    const maybeSell = prev && !open && realPositionOpenRef.current;

    if (maybeBuy || maybeSell) {
      // Debounce buys only — never skip a sell, or we end up holding an
      // untracked position and keep re-buying on every new entry signal.
      if (maybeBuy && now - lastLiveTxAtRef.current < minGap) {
        // Don't advance prevLiveOpenRef so this buy is retried next tick.
        return;
      }
      if (!apiKey) {
        setLivePumpPortalErr(
          appendPumpPortalTradingWalletHint(
            "PumpPortal API key missing — add it in Setup.",
          ),
        );
        prevLiveOpenRef.current = open;
        return;
      }
      lastLiveTxAtRef.current = now;
      prevLiveOpenRef.current = open;
      // Clear real position flag as soon as a sell fires — even if it fails on-chain,
      // we don't want to re-buy while potentially still holding tokens.
      if (maybeSell) realPositionOpenRef.current = false;

      const action = maybeBuy ? "buy" : "sell";
      const tapeSnapshot = paperScalper.botTrades;
      const lastTape = tapeSnapshot[tapeSnapshot.length - 1];
      const exitReason =
        lastTape?.kind === "tape" ? lastTape.exitReason : "order_book_sell";

      void (async () => {
        const poolForTx = maybeBuy
          ? lightningPoolPreference
          : lastBuyVenueRef.current?.mint === mintLoaded &&
              lastBuyVenueRef.current.pool
            ? lastBuyVenueRef.current.pool
            : lightningPoolPreference;

        if (!connected || !secretKey || !publicKey || !heliusKey) {
          setLivePumpPortalErr("Wallet not connected or Helius key missing.");
          if (maybeBuy) realPositionOpenRef.current = false;
          return;
        }

        const res = await postPumpPortalLocalTradeWithFallback(secretKey, heliusKey, {
          publicKey,
          action,
          mint: mintLoaded,
          amount: maybeBuy ? scalperLiveBuySol : "100%",
          denominatedInSol: maybeBuy ? "true" : "false",
          slippage: scalperUserConfig.realSlippagePct,
          priorityFee: scalperUserConfig.realPriorityFeeSol,
          pool: poolForTx,
        });
        if (!res.ok) {
          const tried =
            res.attempts.length > 1
              ? ` — venues tried: ${res.attempts.join(" → ")}`
              : "";
          setLivePumpPortalErr(
            appendPumpPortalTradingWalletHint(res.message + tried),
          );
          // Buy failed — make sure we don't hold a phantom open position flag
          // that would trigger a sell for tokens we never received.
          if (maybeBuy) realPositionOpenRef.current = false;
          return;
        }
        setLivePumpPortalSig(
          `${action.toUpperCase()} confirming… ${res.signature}`,
        );
        setLivePumpPortalErr(null);
        const { confirmed, err } = await confirmLightningTx(res.signature);
        if (!confirmed) {
          setLivePumpPortalSig(null);
          setLivePumpPortalErr(
            appendPumpPortalTradingWalletHint(
              err ?? "Tx did not confirm — check explorer",
            ),
          );
          // Buy not confirmed — same as failed
          if (maybeBuy) realPositionOpenRef.current = false;
          return;
        }

        setLivePumpPortalSig(res.signature);

        if (maybeBuy) {
          // Buy confirmed on-chain — now we genuinely hold tokens
          realPositionOpenRef.current = true;
          pendingLiveBuySigRef.current = res.signature;
          lastBuyVenueRef.current = { mint: mintLoaded, pool: res.poolUsed };
          return;
        }

        // realPositionOpenRef was already cleared synchronously when the sell was dispatched
        lastBuyVenueRef.current = null;

        const buySig = pendingLiveBuySigRef.current;
        pendingLiveBuySigRef.current = null;
        const walletPk = getPumpPortalTradingWalletPubkey();

        if (!buySig) {
          setLivePumpPortalErr(
            appendPumpPortalTradingWalletHint(
              "Sell landed on-chain but session had no confirmed buy signature — add PnL row from Solscan manually if needed.",
            ),
          );
          return;
        }

        if (!walletPk) {
          setLivePumpPortalErr(
            appendPumpPortalTradingWalletHint(
              "Sell confirmed — paste your PumpPortal trading wallet secret in Setup to log on-chain SOL PnL automatically.",
            ),
          );
          return;
        }

        const buyDelta = await fetchWalletSolDeltaSol(buySig, walletPk);
        const sellDelta = await fetchWalletSolDeltaSol(res.signature, walletPk);

        if (buyDelta == null || sellDelta == null) {
          setLivePumpPortalErr(
            appendPumpPortalTradingWalletHint(
              "Sell confirmed — RPC could not read wallet balance deltas yet. Retry Solscan or check your RPC / Helius key.",
            ),
          );
          return;
        }

        const solSpent = Math.max(0, -buyDelta);
        const solReceived = Math.max(0, sellDelta);
        const netSol = buyDelta + sellDelta;
        const roiPct = solSpent > 0 ? (netSol / solSpent) * 100 : 0;

        setLivePumpPortalErr(null);
        const chainTrade: BotTradeRowChain = {
          kind: "chain",
          id: `chain-${res.signature}`,
          entryTs: Date.now(),
          closedAtTs: Date.now(),
          exitReason,
          buySignature: buySig,
          sellSignature: res.signature,
          solSpent,
          solReceived,
          netSol,
          roiPct,
        };
        setLiveChainTrades((prev) => [...prev, chainTrade]);
        appendPersistedTrades([
          {
            ...chainTrade,
            walletPk: walletPk,
            mint: mintLoaded,
          },
        ]);
      })();
      return;
    }

    prevLiveOpenRef.current = open;
  }, [
    paperScalper,
    tradingMode,
    algoSessionActive,
    tradingHalted,
    mintLoaded,
    liveState,
    scalperLiveBuySol,
    lightningPoolPreference,
    scalperUserConfig.realSlippagePct,
    scalperUserConfig.realPriorityFeeSol,
    appendPersistedTrades,
  ]);

  useEffect(() => {
    setChartAnalytics({
      mint: mintLoaded ?? streamMint,
      chartLoading: loading,
      chartError: error,
      yMcCap: mintLoaded ? yMcCap : null,
      chartInterval: mintLoaded || streamMint ? chartInterval : null,
      lastCandle: lastCandleSummary,
      tapeSummary,
      tokenSupplyUi: mintLoaded ? tokenUiSupply : null,
      orderBookConn: liveState,
      orderBookError: liveErr,
      orderBookLastTradeAt: liveLastTradeAt,
      stats: liveStats,
      paperScalper,
      livePumpPortalLastSig: livePumpPortalSig,
      livePumpPortalLastErr: livePumpPortalErr,
      realBotTrades: tradingMode === "real" ? liveChainTrades : [],
    });
  }, [
    mintLoaded,
    streamMint,
    loading,
    error,
    yMcCap,
    chartInterval,
    lastCandleSummary,
    tapeSummary,
    tokenUiSupply,
    liveState,
    liveErr,
    liveLastTradeAt,
    liveStats,
    paperScalper,
    livePumpPortalSig,
    livePumpPortalErr,
    liveChainTrades,
    tradingMode,
    setChartAnalytics,
  ]);

  const walletExecutionMarkersRef = useRef(walletExecutionMarkers);
  useEffect(() => {
    walletExecutionMarkersRef.current = walletExecutionMarkers;
  }, [walletExecutionMarkers]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: BG },
        textColor: FG,
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: 12,
      },
      localization: {
        locale: "en-US",
        priceFormatter: (price: number) => formatUsdCompact(price),
      },
      grid: {
        vertLines: { color: "rgba(0, 0, 0, 0)" },
        horzLines: { color: "rgba(0, 0, 0, 0)" },
      },
      rightPriceScale: {
        borderColor: GRID,
        scaleMargins: { top: 0.35, bottom: 0.35 },
      },
      timeScale: {
        borderColor: GRID,
        timeVisible: true,
        secondsVisible: false,
        lockVisibleTimeRangeOnResize: true,
        /** Dynamic balance: large right offset to push price action toward the center. */
        rightOffset: isMobile ? 50 : 60,
        /** Default spacing for a balanced look. */
        barSpacing: isMobile ? 2.7 : 3,
        /** Prevent 'Giant Candles' on new tokens by capping the maximum width. */
        maxBarSpacing: 14,
        minBarSpacing: 1,
        /** User pans freely; live `setData` must not yank the window when new ticks arrive. */
        shiftVisibleRangeOnNewBar: false,
        fixRightEdge: false,
        fixLeftEdge: false,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: CROSSHAIR,
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: CROSSHAIR_LABEL_BG,
        },
        horzLine: {
          color: CROSSHAIR,
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: CROSSHAIR_LABEL_BG,
        },
      },
    });

    const ts = chart.timeScale();
    const onVisibleRangeChange = () => {
      setZoomStats({
        barSpacing: ts.options().barSpacing,
        rightOffset: Math.round((ts.scrollPosition() as number) || 0),
      });
      // Sync our HTML markers as the user pans or zooms
      updateMarkerPositions();
    };
    ts.subscribeVisibleTimeRangeChange(onVisibleRangeChange);
    ts.subscribeVisibleLogicalRangeChange(updateMarkerPositions);

    const series = chart.addSeries(CandlestickSeries, {
      upColor: UP,
      downColor: DOWN,
      borderVisible: false,
      wickUpColor: UP,
      wickDownColor: DOWN,
    });

    const svixSeries = chart.addSeries(HistogramSeries, {
      color: 'rgba(59, 130, 246, 0.3)',
      priceFormat: { type: 'volume' },
      priceScaleId: '', // overlay
    });
    svixSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    chartRef.current = chart;
    seriesRef.current = series;
    svixSeriesRef.current = svixSeries;
    seriesMarkersPluginRef.current = createSeriesMarkers(series, [], {
      autoScale: true,
      zOrder: "aboveSeries",
    });

    const longPositionPlugin = new LongPositionPlugin();
    series.attachPrimitive(longPositionPlugin);
    longPositionPluginRef.current = longPositionPlugin;

    // ── Lazy historical load on pan-left ─────────────────────────────────────
    // Start fetching an older chunk when the user pans within LOAD_TRIGGER_BARS
    // of the oldest loaded candle (or past it into empty space). Each triggered
    // fetch is completely isolated — one independent request with its own cursor.
    // Works for all intervals: 1s, 1m, 5m, 15m.
    const LOAD_TRIGGER_BARS = 200;

    chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!range || range.from >= LOAD_TRIGGER_BARS) return;
      if (isFetchingOlderRef.current || hasReachedGenesisRef.current) return;
      const mint = mintLoadedRef.current;
      if (!mint) return;
      const oldestTs = oldestLoadedTsRef.current;
      if (oldestTs == null) return;

      isFetchingOlderRef.current = true;

      // Each call is fully isolated — if it fails, the next pan-left retries.
      void fetchPumpCandles(mint, {
        interval: chartIntervalRef.current,
        beforeTs: oldestTs * 1000 - 1, // seconds → ms, step back 1 ms before oldest bar
      })
        .then((result) => {
          isFetchingOlderRef.current = false;

          // Fewer raw fetched bars than the max means we've reached the token's genesis.
          // Use rawFetchedCount (not candles.length) so resampled intervals like "5s"
          // (which compress 1000 1s candles → ~200 5s bars) don't falsely trigger genesis.
          if (result.rawFetchedCount < PUMP_CANDLES_MAX_LIMIT) {
            hasReachedGenesisRef.current = true;
          }

          if (result.candles.length > 0) {
            // Merge older chunk into baseRows — triggers chart setData + scroll preserve.
            setBaseRows((prev) => mergeBaseCandles(prev, result.candles));
          }
        })
        .catch(() => {
          // Release guard on error so the next pan-left can retry.
          isFetchingOlderRef.current = false;
        });
    });

    // Crosshair move handler for tooltip detection
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || !param.time) {
        setTooltipMarker(null);
        return;
      }

      const time = param.time as number;
      const price = param.seriesData.get(series) as
        | { close?: number }
        | undefined;

      if (!price || price.close === undefined) {
        setTooltipMarker(null);
        return;
      }

      // Find nearby wallet execution marker
      const nearbyMarker = walletExecutionMarkersRef.current.find((m) => {
        const markerTime = m.time as number;
        return Math.abs(markerTime - time) < bucketSec * 2; // Within 2 bars
      });

      if (nearbyMarker && nearbyMarker.profile) {
        const rect = el.getBoundingClientRect();
        setTooltipMarker({
          marker: nearbyMarker,
          position: {
            x: rect.left + param.point.x,
            y: rect.top + param.point.y,
          },
        });
      } else {
        setTooltipMarker(null);
      }
    });

    return () => {
      seriesMarkersPluginRef.current?.detach();
      seriesMarkersPluginRef.current = null;
      if (longPositionPluginRef.current && seriesRef.current) {
        seriesRef.current.detachPrimitive(longPositionPluginRef.current);
      }
      longPositionPluginRef.current = null;
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      svixSeriesRef.current = null;
    };
  }, [isMobile, bucketSec]);

  /** Axis labels + crosshair use the viewer’s zone (auto) or a chosen IANA zone. */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const showSeconds = bucketSec < 60;
    chart.applyOptions({
      localization: {
        locale,
        priceFormatter: (price: number) => formatUsdCompact(price),
        timeFormatter: (t: Time) =>
          formatChartCrosshairTime(t, effectiveTz, locale, showSeconds),
      },
      timeScale: {
        secondsVisible: showSeconds,
        tickMarkFormatter: (
          time: Time,
          tickMarkType: TickMarkType,
          loc: string,
        ) => formatChartTickMark(time, tickMarkType, loc, effectiveTz),
      },
    });
  }, [effectiveTz, bucketSec, locale]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const isLight = theme === "light";
    const bg = isLight ? "#fcfcfc" : "rgba(0, 0, 0, 0)";
    const grid = isLight ? "#e0e0e0" : "rgba(255, 255, 255, 0.05)";
    const fg = isLight ? "#2c2c2c" : "rgba(255, 255, 255, 0.8)";
    const crosshair = isLight
      ? "rgba(44, 44, 44, 0.4)"
      : "rgba(255, 255, 255, 0.2)";
    const crosshairLabelBg = isLight ? "#f3f3f3" : "#0b0e11";

    chart.applyOptions({
      layout: {
        background: { color: bg },
        textColor: fg,
      },
      grid: {
        vertLines: { color: "rgba(0, 0, 0, 0)" },
        horzLines: { color: "rgba(0, 0, 0, 0)" },
      },
      rightPriceScale: {
        borderColor: grid,
      },
      timeScale: {
        borderColor: grid,
      },
      crosshair: {
        vertLine: {
          color: crosshair,
          labelBackgroundColor: crosshairLabelBg,
        },
        horzLine: {
          color: crosshair,
          labelBackgroundColor: crosshairLabelBg,
        },
      },
    });
  }, [theme]);

  useEffect(() => {
    return () => {
      setChartAnalytics({ paperScalper: null });
    };
  }, [setChartAnalytics]);

  useEffect(() => {
    const mint = normalizeMint(caMintInput);
    const valid = mint && looksLikeSolMint(mint) ? mint : "";
    if (valid) {
      // Immediate switch when clicking active/closed positions or tokens
      setDebouncedMint(valid);
      return;
    }
    const t = window.setTimeout(() => {
      setDebouncedMint("");
    }, 300);
    return () => window.clearTimeout(t);
  }, [caMintInput]);

  useEffect(() => {
    didFitRef.current = false;
  }, [activeMint, chartInterval]);

  // Single-page initial load (1,000 candles) covers initial viewport in ~60ms without secondary network hops.
  const pagesForInterval = (_iv: ChartInterval) => 1;

  // Background prefetch candles for active positions and recent trades so chart switches are always 0ms instant
  useEffect(() => {
    const mints: string[] = [];
    if (serverOpenPositions && Array.isArray(serverOpenPositions)) {
      for (const p of serverOpenPositions) {
        if (p.mint && !mints.includes(p.mint)) mints.push(p.mint);
      }
    }
    if (serverPastTrades && Array.isArray(serverPastTrades)) {
      for (const t of serverPastTrades) {
        if (t.mint && !mints.includes(t.mint)) mints.push(t.mint);
      }
    }
    if (mints.length > 0) {
      prefetchBatchCandles(mints, chartInterval, 10);
    }
  }, [serverOpenPositions, serverPastTrades, chartInterval]);

  useEffect(() => {
    // Reset lazy-load cursors whenever mint or interval changes.
    isFetchingOlderRef.current = false;
    hasReachedGenesisRef.current = false;
    oldestLoadedTsRef.current = null;
    // Allow a fresh auto-detect run for the new mint/interval.
    autoDetectedMintRef.current = "";

    if (!activeMint) {
      fetchGenRef.current += 1;
      setMintLoaded(null);
      setBaseRows([]);
      setTokenUiSupply(null);
      setError(null);
      setLoading(false);
      return;
    }

    const gen = ++fetchGenRef.current;

    // 1. Instant Cache Hit: 0ms switch
    const cached = getCachedCandles(activeMint, chartInterval);
    if (cached && cached.candles.length > 0) {
      setMintLoaded(activeMint);
      setYMcCap(cached.yAxisIsMarketCapUsd);
      if (cached.tokenUiSupply != null) setTokenUiSupply(cached.tokenUiSupply);
      setBaseRows(cached.candles);
      setLoading(false);
      setError(null);
      return;
    }

    // 2. Instant Optimistic Seed from Position/Trade metadata or Nursery memory (zero blank-screen flash)
    const activePos = serverOpenPositions?.find((p: any) => p.mint === activeMint);
    const pastTrade = serverPastTrades?.find((p: any) => p.mint === activeMint);
    const pos = activePos || pastTrade;
    const nToken = nursery.getNurseryToken(activeMint);
    const meta = tokenMetaCache.get(activeMint);
    const effSolPrice = Number(solPrice) > 0 ? Number(solPrice) : 150;
    let seeded = false;

    if (pos) {
      const entryMc = pos.entryMcUsd || (pos.entryPrice ? pos.entryPrice * 1e9 : null);
      const exitOrCurrentMc = pos.currentMcUsd || pos.exitMcUsd || entryMc;
      const highMc = pos.highestMcUsd || Math.max(entryMc || 0, exitOrCurrentMc || 0);
      const lowMc = pos.lowestMcUsd || Math.min(entryMc || Infinity, exitOrCurrentMc || Infinity);
      const entrySec = pos.entryTime ? Math.floor(pos.entryTime / 1000) : (Math.floor(Date.now() / 1000) - 60);
      const nowSec = pos.timestamp ? Math.floor(pos.timestamp / 1000) : Math.floor(Date.now() / 1000);
      
      if (entryMc && Number.isFinite(entryMc)) {
        const seedCandles = [
          {
            time: entrySec as UTCTimestamp,
            open: entryMc,
            high: highMc && Number.isFinite(highMc) ? highMc : entryMc,
            low: lowMc && Number.isFinite(lowMc) ? lowMc : entryMc,
            close: exitOrCurrentMc && Number.isFinite(exitOrCurrentMc) ? exitOrCurrentMc : entryMc,
          },
          {
            time: Math.max(entrySec + 1, nowSec) as UTCTimestamp,
            open: entryMc,
            high: highMc && Number.isFinite(highMc) ? highMc : entryMc,
            low: lowMc && Number.isFinite(lowMc) ? lowMc : entryMc,
            close: exitOrCurrentMc && Number.isFinite(exitOrCurrentMc) ? exitOrCurrentMc : entryMc,
          }
        ];
        isSyntheticRef.current = true;
        setMintLoaded(activeMint);
        setYMcCap(true);
        setBaseRows(seedCandles);
        setLoading(false);
        seeded = true;
      }
    } else if (nToken || meta) {
      let curMc = 0;
      let peakMc = 0;
      let initMc = 0;
      let firstSec = Math.floor(Date.now() / 1000) - 120;
      let lastSec = Math.floor(Date.now() / 1000);

      if (nToken) {
        curMc = (nToken.marketCapSol || 0) * effSolPrice;
        peakMc = (nToken.peakMcSol || nToken.marketCapSol || 0) * effSolPrice;
        initMc = 30 * effSolPrice;
        if (nToken.firstSeenMs) firstSec = Math.floor(nToken.firstSeenMs / 1000);
        if (nToken.lastTradeMs) lastSec = Math.floor(nToken.lastTradeMs / 1000);
      } else if (meta) {
        curMc = (meta as any).usd_market_cap || (((meta as any).market_cap || 0) * effSolPrice);
        peakMc = curMc * 1.1;
        initMc = curMc * 0.9;
      }

      if (curMc > 0 && Number.isFinite(curMc)) {
        const seedCandles = [
          {
            time: Math.min(firstSec, lastSec - 1) as UTCTimestamp,
            open: initMc > 0 ? initMc : curMc,
            high: Math.max(peakMc, curMc, initMc || curMc),
            low: Math.min(initMc > 0 ? initMc : curMc, curMc),
            close: curMc,
          },
          {
            time: Math.max(firstSec + 1, lastSec) as UTCTimestamp,
            open: initMc > 0 ? initMc : curMc,
            high: Math.max(peakMc, curMc),
            low: Math.min(initMc > 0 ? initMc : curMc, curMc),
            close: curMc,
          }
        ];
        isSyntheticRef.current = true;
        setMintLoaded(activeMint);
        setYMcCap(true);
        setBaseRows(seedCandles);
        setLoading(false);
        seeded = true;
      }
    }

    if (!seeded) {
      setMintLoaded(activeMint);
      setBaseRows([]);
      setLoading(true);
    }
    setError(null);

    void (async () => {
      try {
        const result = await fetchPumpCandlesPaged(activeMint, {
          interval: chartInterval,
          pages: pagesForInterval(chartInterval),
        });
        if (gen !== fetchGenRef.current) return;

        let finalCandles = result.candles;
        // Fallback: If external API has 0 candles, synthesize candles from active or past trades or nursery
        if (finalCandles.length === 0) {
          if (pos) {
            const entryMc = pos.entryMcUsd || (pos.entryPrice ? pos.entryPrice * 1e9 : null);
            const exitOrCurrentMc = pos.currentMcUsd || pos.exitMcUsd || entryMc;
            const highMc = pos.highestMcUsd || Math.max(entryMc || 0, exitOrCurrentMc || 0);
            const lowMc = pos.lowestMcUsd || Math.min(entryMc || Infinity, exitOrCurrentMc || Infinity);
            const entrySec = pos.entryTime ? Math.floor(pos.entryTime / 1000) : (Math.floor(Date.now() / 1000) - 60);
            const nowSec = pos.timestamp ? Math.floor(pos.timestamp / 1000) : Math.floor(Date.now() / 1000);
            
            if (entryMc && Number.isFinite(entryMc)) {
              finalCandles = [
                {
                  time: entrySec as UTCTimestamp,
                  open: entryMc,
                  high: highMc && Number.isFinite(highMc) ? highMc : entryMc,
                  low: lowMc && Number.isFinite(lowMc) ? lowMc : entryMc,
                  close: exitOrCurrentMc && Number.isFinite(exitOrCurrentMc) ? exitOrCurrentMc : entryMc,
                },
                {
                  time: Math.max(entrySec + 1, nowSec) as UTCTimestamp,
                  open: entryMc,
                  high: highMc && Number.isFinite(highMc) ? highMc : entryMc,
                  low: lowMc && Number.isFinite(lowMc) ? lowMc : entryMc,
                  close: exitOrCurrentMc && Number.isFinite(exitOrCurrentMc) ? exitOrCurrentMc : entryMc,
                }
              ];
            }
          } else if (nToken || meta) {
            let curMc = 0;
            let peakMc = 0;
            let initMc = 0;
            let firstSec = Math.floor(Date.now() / 1000) - 120;
            let lastSec = Math.floor(Date.now() / 1000);

            if (nToken) {
              curMc = (nToken.marketCapSol || 0) * effSolPrice;
              peakMc = (nToken.peakMcSol || nToken.marketCapSol || 0) * effSolPrice;
              initMc = 30 * effSolPrice;
              if (nToken.firstSeenMs) firstSec = Math.floor(nToken.firstSeenMs / 1000);
              if (nToken.lastTradeMs) lastSec = Math.floor(nToken.lastTradeMs / 1000);
            } else if (meta) {
              curMc = (meta as any).usd_market_cap || (((meta as any).market_cap || 0) * effSolPrice);
              peakMc = curMc * 1.1;
              initMc = curMc * 0.9;
            }

            if (curMc > 0 && Number.isFinite(curMc)) {
              finalCandles = [
                {
                  time: Math.min(firstSec, lastSec - 1) as UTCTimestamp,
                  open: initMc > 0 ? initMc : curMc,
                  high: Math.max(peakMc, curMc, initMc || curMc),
                  low: Math.min(initMc > 0 ? initMc : curMc, curMc),
                  close: curMc,
                },
                {
                  time: Math.max(firstSec + 1, lastSec) as UTCTimestamp,
                  open: initMc > 0 ? initMc : curMc,
                  high: Math.max(peakMc, curMc),
                  low: Math.min(initMc > 0 ? initMc : curMc, curMc),
                  close: curMc,
                }
              ];
            }
          }
        }

        const finalResult = { ...result, candles: finalCandles };
        if (finalCandles.length > 0) {
          setCachedCandles(activeMint, chartInterval, finalResult);
        }
        setMintLoaded(activeMint);
        setYMcCap(result.yAxisIsMarketCapUsd);
        setTokenUiSupply(result.tokenUiSupply);

        if (isSyntheticRef.current && finalCandles.length > 2) {
          isSyntheticRef.current = false;
          didFitRef.current = false;
        }
        setBaseRows(finalCandles);
      } catch (e) {
        if (gen !== fetchGenRef.current) return;
        setMintLoaded(activeMint);
        if (!seeded) {
          setBaseRows([]);
          setTokenUiSupply(null);
        }
        setError(null);
      } finally {
        if (gen === fetchGenRef.current) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMint, chartInterval]);

  useEffect(() => {
    if (!mintLoaded) return;
    let cancelled = false;
    const poll = window.setInterval(() => {
      void (async () => {
        try {
          // Fetch only the latest page and MERGE into the existing base rows so
          // the full history (up to 10 k candles) is never discarded by a poll.
          const {
            candles: fresh,
            yAxisIsMarketCapUsd,
            tokenUiSupply: supply,
          } = await fetchPumpCandles(mintLoaded, { interval: chartInterval });
          if (cancelled) return;
          setBaseRows((prev) => {
            const merged = mergeBaseCandles(prev, fresh);
            setCachedCandles(mintLoaded, chartInterval, {
              candles: merged,
              yAxisIsMarketCapUsd,
              tokenUiSupply: supply ?? tokenUiSupply,
              rawFetchedCount: merged.length,
            });
            return merged;
          });
          setYMcCap(yAxisIsMarketCapUsd);
          if (supply != null) setTokenUiSupply(supply);
        } catch {
          /* keep last series */
        }
      })();
    }, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [mintLoaded, chartInterval]);

  useLayoutEffect(() => {
    const s = seriesRef.current;
    const chart = chartRef.current;
    if (!s || !chart) return;

    const mintChanged = activeMint !== lastRenderedMintRef.current;
    lastRenderedMintRef.current = activeMint;

    if (mintChanged) {
      didFitRef.current = false;
      // Immediately remove all old server paper trade price lines from previous token
      for (const line of serverPaperTradeLinesRef.current) {
        try { s.removePriceLine(line); } catch {}
      }
      serverPaperTradeLinesRef.current = [];
    }

    if (chartRows.length === 0) {
      if (baseRows.length === 0 || mintChanged) {
        s.setData([]);
      }
      return;
    }

    const ts = chart.timeScale();
    /**
     * Gap from the chart’s right edge to the latest bar (in bar widths). Unlike `setVisibleRange`,
     * this survives `setData` without clamping `to` onto the last candle — so zoom-out “air” on the
     * right stays put across live merges and REST polls.
     */
    const scrollBefore = ts.scrollPosition();

    const baseRowsChanged = baseRows !== prevBaseRowsRef.current || mintChanged;
    prevBaseRowsRef.current = baseRows;

    if (baseRowsChanged || mintChanged) {
      // Full REST refresh: setData wipes all createPriceLine handles — re-draw immediately after.
      s.setData(chartRows);
      setLoading(false);
      redrawBounceLinesOnSeries(s);

      // Re-draw server paper trade lines — setData does NOT wipe price lines automatically, so we must clean them up first!
      for (const line of serverPaperTradeLinesRef.current) {
        try { s.removePriceLine(line); } catch {}
      }
      serverPaperTradeLinesRef.current = [];

      const activePos = serverOpenPositions?.find((p: any) => p.mint === activeMint);
      const sells = serverPastTrades.filter(t => t.action === 'sell');
      
      let sellIndex = sells.length;
      sells.forEach(sell => {
        const pnlPct = sell.pnlPct || 0;
        const isProfit = pnlPct >= 0;
        const pnlLabel = `${isProfit ? "+" : ""}${(pnlPct * 100).toFixed(2)}%`;
        const exitLine = s.createPriceLine({
          price: sell.exitMcUsd || sell.entryMcUsd,
          color: isProfit ? "#10b981" : "#ef4444",
          lineWidth: 1,
          lineStyle: LineStyle.SparseDotted,
          axisLabelVisible: true,
          title: `EXIT #${sellIndex}  ${pnlLabel}`,
        });
        serverPaperTradeLinesRef.current.push(exitLine);

        const entryLine = s.createPriceLine({
          price: sell.entryMcUsd,
          color: "#9ca3af",
          lineWidth: 1,
          lineStyle: LineStyle.SparseDotted,
          axisLabelVisible: true,
          title: `ENTRY #${sellIndex}`,
        });
        serverPaperTradeLinesRef.current.push(entryLine);
        
        sellIndex--;
      });

      if (activePos) {
        const pnlPct = activePos.pnlPct ?? 0;
        const isProfit = pnlPct >= 0;
        const pnlLabel = `${isProfit ? "+" : ""}${(pnlPct * 100).toFixed(2)}%`;
        const line = s.createPriceLine({
          price: activePos.entryMcUsd,
          color: isProfit ? "#22d3ee" : "#f87171",
          lineWidth: 2,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: `ACTIVE ENTRY  ${pnlLabel}`,
        });
        serverPaperTradeLinesRef.current.push(line);
      }

      try { ts.fitContent(); } catch {}

    } else {
      // Live tick only: series.update() preserves price lines, no re-draw needed.
      const last = chartRows[chartRows.length - 1];
      if (last) {
        try {
          // Ensure time is a number, not an object
          if (typeof last.time !== "number") {
            console.error(
              "[Chart Update] Invalid time type:",
              typeof last.time,
              last,
            );
            return;
          }
          s.update(last);
        } catch (err) {
          console.error("[Chart Update] Failed to update chart:", err, last);
        }
      }
    }

    if (!didFitRef.current) {
      // Fit immediately on first load of the series
      try {
        ts.fitContent();
      } catch {
        /* ignore */
      }
      didFitRef.current = true;
      return;
    }

    const restoreScroll = () => {
      try {
        ts.scrollToPosition(scrollBefore, false);
      } catch {
        /* ignore */
      }
    };

    restoreScroll();
    requestAnimationFrame(() => {
      restoreScroll();
      updateMarkerPositions();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRows, activeMint]);

  // ── SERVER PAPER TRADE LINE UPDATE ──
  // Listen to serverOpenPositions and serverPastTrades to update the lines when PnL changes or new trades appear
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;

    // Remove old lines
    for (const line of serverPaperTradeLinesRef.current) {
      try { series.removePriceLine(line); } catch {}
    }
    serverPaperTradeLinesRef.current = [];

    const activePos = serverOpenPositions?.find((p: any) => p.mint === activeMint);
    const sells = serverPastTrades.filter(t => t.action === 'sell');
    
    let sellIndex = sells.length;
    sells.forEach(sell => {
      const pnlPct = sell.pnlPct || 0;
      const isProfit = pnlPct >= 0;
      const pnlLabel = `${isProfit ? "+" : ""}${(pnlPct * 100).toFixed(2)}%`;
      
      const exitLine = series.createPriceLine({
        price: sell.exitMcUsd || sell.entryMcUsd,
        color: isProfit ? "#10b981" : "#ef4444",
        lineWidth: 1,
        lineStyle: LineStyle.SparseDotted,
        axisLabelVisible: true,
        title: `EXIT #${sellIndex}  ${pnlLabel}`,
      });
      serverPaperTradeLinesRef.current.push(exitLine);

      const entryLine = series.createPriceLine({
        price: sell.entryMcUsd,
        color: "#9ca3af",
        lineWidth: 1,
        lineStyle: LineStyle.SparseDotted,
        axisLabelVisible: true,
        title: `ENTRY #${sellIndex}`,
      });
      serverPaperTradeLinesRef.current.push(entryLine);

      sellIndex--;
    });

    if (activePos) {
      const entryPrice = activePos.entryMcUsd;
      const pnlPct = activePos.pnlPct ?? 0;
      const isProfit = pnlPct >= 0;
      const pnlLabel = `${isProfit ? "+" : ""}${(pnlPct * 100).toFixed(2)}%`;

      const line = series.createPriceLine({
        price: entryPrice,
        color: isProfit ? "#22d3ee" : "#f87171",
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: `ACTIVE ENTRY  ${pnlLabel}`,
      });

      serverPaperTradeLinesRef.current.push(line);
    }
  }, [serverOpenPositions, serverPastTrades, activeMint]);

  // Prefetch 1s candles for bounce detection — always 1s regardless of chart interval,
  // so the ZigZag algo and vision API always see maximum granularity.
  // Only re-fetches when the mint changes, NOT on interval switches — no reason to
  // redo a 10-page 1s fetch just because the user toggled from 5s to 1m.
  useEffect(() => {
    if (!mintLoaded) {
      setFloorDetectionRows([]);
      setFloorCandlesStatus("idle");
      return;
    }
    const gen = ++floorFetchGenRef.current;
    setFloorDetectionRows([]);
    setFloorCandlesStatus("loading");
    void (async () => {
      try {
        // Use the in-memory cache if the chart initial load already fetched 1s bars.
        // 10 pages × 1000 bars = 10,000 × 1 s ≈ 2.8 h of price history.
        const cached1s = getCachedCandles(mintLoaded, "1s");
        const { candles } =
          cached1s ??
          (await fetchPumpCandlesPaged(mintLoaded, {
            interval: "1s",
            pages: 2,
          }));
        if (gen !== floorFetchGenRef.current) return;
        setFloorDetectionRows(candles);
        setFloorCandlesStatus("ready");
      } catch {
        if (gen !== floorFetchGenRef.current) return;
        setFloorCandlesStatus("ready");
      }
    })();
    // chartInterval intentionally omitted — floor data is always 1s and switching
    // the chart's display interval must NOT trigger a redundant 10-page 1s re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mintLoaded]);

  // Track the last manual-refresh tick so we can distinguish user clicks from
  // effect re-runs triggered by candle updates.
  const prevBounceSuggestionTickRef = useRef(0);
  // Per-mint flag: once we auto-detect on first load we never auto-detect again
  // for that mint (until a new CA is entered). Vision always stays manual-only.
  const autoDetectedMintRef = useRef<string>("");

  // Bounce zone detection.
  //   • First load for a mint  → algo runs automatically (free, no API credits).
  //   • Manual "Refresh" click → algo + vision both run (costs API credits).
  //   • Subsequent candle polls → neither runs (isManualRefresh=false AND mint already auto-detected).
  useEffect(() => {
    if (!mintLoaded || floorDetectionRows.length < MIN_BOOTSTRAP_CANDLES)
      return;

    const isManualRefresh =
      bounceSuggestionTick > prevBounceSuggestionTickRef.current;
    prevBounceSuggestionTickRef.current = bounceSuggestionTick;

    const isFirstLoad = autoDetectedMintRef.current !== mintLoaded;

    // Nothing to do — not a manual click and this mint was already auto-detected.
    if (!isManualRefresh && !isFirstLoad) return;

    const currentPrice =
      floorDetectionRows[floorDetectionRows.length - 1]?.close;
    const algoZones = detectBounceZones(floorDetectionRows, currentPrice);
    setDetectedZones(mintLoaded, algoZones);

    if (isFirstLoad) {
      // Mark this mint as auto-detected so subsequent poll re-runs are ignored.
      autoDetectedMintRef.current = mintLoaded;
      // Stop here on first load — vision is credit-burning and stays manual only.
      return;
    }

    // --- Manual refresh path: also run vision detection ---
    if (!model.apiKey.trim()) return;

    setChartAnalytics({
      visionDetectStatus: "loading",
      visionDetectError: null,
    });

    void detectBounceZonesVision(floorDetectionRows, model, currentPrice).then(
      (result) => {
        if (result.ok && result.prices.length > 0) {
          const mintNow = mintLoaded;
          const modelTag =
            result.modelUsed.split("/").pop()?.slice(0, 12) ?? "ai";
          const visionOnlyZones = result.prices.map((price) => ({
            price,
            confluenceScore: 0.5,
            sources: `vision·${modelTag}`,
            strength: 0.5,
            lastTouchAgo: 0,
            isSupport: true,
            touches: 0,
          }));
          setDetectedZones(mintNow, [...algoZones, ...visionOnlyZones]);
          setChartAnalytics({
            visionDetectStatus: "done",
            visionDetectError: null,
          });
        } else {
          setChartAnalytics({
            visionDetectStatus: result.ok ? "done" : "error",
            visionDetectError: result.ok ? null : result.error,
          });
        }
      },
    );
    // chartInterval intentionally omitted — floor rows are always 1s so interval
    // changes carry no new information for the detector.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorDetectionRows, mintLoaded, bounceSuggestionTick, setDetectedZones]);

  // Draw bounce zone price lines on the chart — fires when zones are (re)computed or mint changes.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !mintLoaded) return;

    // Explicitly remove old lines before re-drawing (in case setData wasn't called this cycle).
    const si = series as unknown as { _bounceLines?: BouncePriceLineHandle[] };
    if (si._bounceLines) {
      for (const line of si._bounceLines) {
        try {
          series.removePriceLine(line);
        } catch {
          /* ignore */
        }
      }
    }

    redrawBounceLinesOnSeries(series);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounceZones, mintLoaded]);

  // Drag bounce zone lines: pointer capture + disable chart pan/zoom while dragging;
  // move the native price line directly — React state updates once on release (smooth).
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const SNAP_PX = 10;

    const endDrag = () => {
      const chart = chartRef.current;
      const id = draggingZoneIdRef.current;
      const price = pendingDragPriceRef.current;
      const pid = dragPointerIdRef.current;

      draggingZoneIdRef.current = null;
      dragPointerIdRef.current = null;
      pendingDragPriceRef.current = null;
      host.style.cursor = "";

      if (chart) {
        chart.applyOptions({ handleScroll: true, handleScale: true });
      }
      if (pid != null) {
        try {
          host.releasePointerCapture(pid);
        } catch {
          /* not capturing */
        }
      }
      if (id != null && price != null && price > 0) {
        // Hard clamp: zone can never be at or above 93 % of live price (7 % minimum gap).
        const liveNow = livePriceRef.current;
        const clamped =
          liveNow != null && liveNow > 0
            ? Math.min(price, liveNow * 0.93)
            : price;
        updateBounceZonePriceRef.current(id, clamped);
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const chart = chartRef.current;
      const series = seriesRef.current;
      const mint = mintLoadedRef.current;
      if (!chart || !series || !mint) return;

      const rect = host.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const zones = bounceZonesRef.current.filter(
        (z: UserBounceZone) => z.mint === mint && z.enabled,
      );
      for (const zone of zones) {
        const lineY = series.priceToCoordinate(zone.price);
        if (lineY != null && Math.abs(y - lineY) <= SNAP_PX) {
          e.preventDefault();
          e.stopPropagation();
          chart.applyOptions({ handleScroll: false, handleScale: false });
          draggingZoneIdRef.current = zone.id;
          dragPointerIdRef.current = e.pointerId;
          pendingDragPriceRef.current = zone.price;
          host.setPointerCapture(e.pointerId);
          host.style.cursor = "ns-resize";
          return;
        }
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const series = seriesRef.current;
      const id = draggingZoneIdRef.current;
      const rect = host.getBoundingClientRect();
      const y = e.clientY - rect.top;

      if (id && series) {
        const price = series.coordinateToPrice(y);
        const handle = bouncePriceLineHandlesRef.current.get(id);
        if (price != null && price > 0 && handle) {
          handle.applyOptions({ price });
          pendingDragPriceRef.current = price;
        }
        return;
      }

      const mint = mintLoadedRef.current;
      if (!series || !mint) return;
      const zones = bounceZonesRef.current.filter(
        (z: UserBounceZone) => z.mint === mint && z.enabled,
      );
      const near = zones.some((zone: UserBounceZone) => {
        const lineY = series.priceToCoordinate(zone.price);
        return lineY != null && Math.abs(y - lineY) <= SNAP_PX;
      });
      host.style.cursor = near ? "ns-resize" : "";
    };

    const onWindowPointerEnd = (e: PointerEvent) => {
      if (dragPointerIdRef.current !== e.pointerId) return;
      endDrag();
    };

    host.addEventListener("pointerdown", onPointerDown, { capture: true });
    host.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onWindowPointerEnd);
    window.addEventListener("pointercancel", onWindowPointerEnd);

    return () => {
      const chart = chartRef.current;
      const pid = dragPointerIdRef.current;
      draggingZoneIdRef.current = null;
      dragPointerIdRef.current = null;
      pendingDragPriceRef.current = null;
      host.style.cursor = "";
      if (chart) {
        chart.applyOptions({ handleScroll: true, handleScale: true });
      }
      if (pid != null) {
        try {
          host.releasePointerCapture(pid);
        } catch {
          /* ignore */
        }
      }
      host.removeEventListener("pointerdown", onPointerDown, { capture: true });
      host.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onWindowPointerEnd);
      window.removeEventListener("pointercancel", onWindowPointerEnd);
    };
  }, []);



  // Separate Canvas Dots (Paper Trading Bot) & HTML Profile Images (Wallets)
  useEffect(() => {
    const api = seriesMarkersPluginRef.current;
    if (!api) return;

    // 1. PAPER TRADES: Keep standard canvas dots for bot trades
    const canvasMarkers: SeriesMarker<UTCTimestamp>[] = [];

    if (
      scalperEngineRunning &&
      paperScalper &&
      paperScalper.paperMarkers.length > 0 &&
      chartRows.length > 0
    ) {
      const activeEntryTs = paperScalper.status === "in_trade" && paperScalper.paperMarkers.length > 0
        ? paperScalper.paperMarkers[paperScalper.paperMarkers.length - 1].timeSec
        : null;

      for (const m of paperScalper.paperMarkers) {
        const barT = alignPaperMarkerToBars(m.timeSec, bucketSec, chartRows);
        if (barT == null) continue;
        
        // Grey out old trades if it's not the active entry
        let color = m.side === "buy" ? UP : DOWN;
        if (m.side === "sell" || m.timeSec !== activeEntryTs) {
          color = "#6b7280"; // Grey for historical trades
        }

        canvasMarkers.push({
          time: barT as UTCTimestamp,
          position: m.side === "buy" ? "belowBar" : "aboveBar",
          shape: "circle",
          color,
          size: 0.55,
          id: `paper-${m.side}-${m.timeSec}-${barT}`,
        });
      }
    }

    // Add server bot trades
    if (serverPastTrades && serverPastTrades.length > 0 && chartRows.length > 0) {
      // Show closed trades in grey
      for (const t of serverPastTrades) {
        if (t.entryTime) {
          const entryBarT = alignPaperMarkerToBars(t.entryTime / 1000, bucketSec, chartRows);
          if (entryBarT) {
            canvasMarkers.push({
              time: entryBarT as UTCTimestamp,
              position: "belowBar",
              shape: "arrowUp",
              color: "#6b7280", // Grey for historical
              size: 1,
              id: `svr-buy-${t.entryTime}-${entryBarT}`,
            });
          }
        }
        if (t.timestamp && t.action === 'sell') {
          const exitBarT = alignPaperMarkerToBars(t.timestamp / 1000, bucketSec, chartRows);
          if (exitBarT) {
            canvasMarkers.push({
              time: exitBarT as UTCTimestamp,
              position: "aboveBar",
              shape: "arrowDown",
              color: "#6b7280", // Grey for historical
              size: 1,
              id: `svr-sell-${t.timestamp}-${exitBarT}`,
            });
          }
        }
      }
    }

    if (serverOpenPositions && serverOpenPositions.length > 0 && chartRows.length > 0) {
      const activePos = serverOpenPositions.find((p: any) => p.mint === activeMint);
      if (activePos && activePos.timestamp) {
        const entryBarT = alignPaperMarkerToBars(activePos.timestamp / 1000, bucketSec, chartRows);
        if (entryBarT) {
          canvasMarkers.push({
            time: entryBarT as UTCTimestamp,
            position: "belowBar",
            shape: "arrowUp",
            color: UP, // Colored for active
            size: 1,
            id: `svr-active-${activePos.timestamp}-${entryBarT}`,
          });
        }
      }
    }

    canvasMarkers.sort((a, b) => (a.time as number) - (b.time as number));
    api.setMarkers(canvasMarkers);

    // 2. WALLET TRADES: Process into HTML Overlay Groups
    const groups = new Map<string, any>();

    if (walletExecutionMarkers.length > 0 && chartRows.length > 0) {
      for (const m of walletExecutionMarkers) {
        // Show all trades, no longer filtering to only whales

        const barT = alignPaperMarkerToBars(
          m.time as number,
          bucketSec,
          chartRows,
        );
        if (barT == null) continue;

        const bar = chartRows.find((r) => r.time === barT);
        if (!bar) continue;

        const isBuy = m.position === "belowBar";
        const price = isBuy ? bar.low : bar.high;

        const key = `${barT}-${isBuy ? "buy" : "sell"}`;
        if (!groups.has(key)) {
          groups.set(key, {
            id: key,
            time: barT,
            price,
            isBuy,
            wallets: [],
          });
        }
        groups.get(key).wallets.push(m);
      }
    }

    setRenderableGroups(Array.from(groups.values()));
  }, [
    paperScalper,
    scalperEngineRunning,
    walletExecutionMarkers,
    chartRows,
    bucketSec,
    serverPastTrades,
    serverOpenPositions,
    activeMint,
  ]);

  // Update Proposed Trade Position (Risk/Reward Box)
  useEffect(() => {
    if (!longPositionPluginRef.current || chartRows.length === 0) return;

    // Only propose if NOT in a trade
    if (paperScalper?.status === "in_trade") {
      longPositionPluginRef.current.setData(null);
      return;
    }

    // Lookback window for swing low and previous high
    const lookback = 30;
    if (chartRows.length >= lookback) {
      const recentBars = chartRows.slice(-lookback);
      let minLow = Infinity;
      let maxHigh = -Infinity;
      
      for (const bar of recentBars) {
        if (bar.low < minLow) minLow = bar.low;
        if (bar.high > maxHigh) maxHigh = bar.high;
      }

      const currentPrice = chartRows[chartRows.length - 1].close;

      // Ensure minLow < currentPrice < maxHigh to make a valid long setup
      if (minLow < currentPrice && maxHigh > currentPrice) {
        const startLogical = chartRows.length - 10;
        const endLogical = chartRows.length + 10;

        const data: LongPositionData = {
          entryPrice: currentPrice,
          slPrice: minLow,
          tpPrice: maxHigh,
          startLogical: startLogical > 0 ? startLogical : 0,
          endLogical: endLogical,
        };
        longPositionPluginRef.current.setData(data);
      } else {
        longPositionPluginRef.current.setData(null);
      }
    } else {
      longPositionPluginRef.current.setData(null);
    }
  }, [chartRows, paperScalper?.status]);

  // Animate Algoat Vision and Swing low lines to pulse their opacity
  useEffect(() => {
    let animationFrameId: number;
    const start = Date.now();
    const animate = () => {
      const now = Date.now();
      const pulse = Math.abs(Math.sin((now - start) / 500)); // 0 to 1
      const minOpacity = 0.3;
      const maxOpacity = 0.9;
      const currentOpacity = minOpacity + pulse * (maxOpacity - minOpacity);
      
      for (const line of bouncePriceLineHandlesRef.current.values()) {
        const opts = (line as any).options?.();
        if (opts && opts.title) {
          const title = opts.title.toLowerCase();
          if (title.includes("vision") || title.includes("swing")) {
            line.applyOptions({ color: `rgba(16, 185, 129, ${currentOpacity.toFixed(3)})` });
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  useEffect(() => {
    setIsTrading(paperScalper?.status === "in_trade");
    return () => setIsTrading(false);
  }, [paperScalper?.status, setIsTrading]);

  const lastClosedCountRef = useRef(0);
  useEffect(() => {
    lastClosedCountRef.current = 0; // Reset on mint change
  }, [mintLoaded]);

  useEffect(() => {
    if (!paperScalper || tradingMode !== "paper" || !mintLoaded) return;
    const closed = paperScalper.botTrades;
    if (closed.length > lastClosedCountRef.current) {
      const newOnes = closed.slice(lastClosedCountRef.current);
      const meta = tokenMetaCache.get(mintLoaded);
      appendPersistedTrades(
        newOnes.map((t: any) => ({
          ...t,
          mint: mintLoaded,
          walletPk: "PAPER",
          symbol: meta?.symbol,
          imageUri: meta?.imageUri,
        })),
      );
      lastClosedCountRef.current = closed.length;
    }
  }, [paperScalper?.botTrades, tradingMode, mintLoaded, appendPersistedTrades]);

  return (
    <div
      className="flex h-full min-h-[320px] flex-col overflow-hidden rounded-2xl"
      style={{ background: "var(--color-bg-editor)" }}
    >
      <WalletConnectModal
        isOpen={isWalletModalOpen}
        onClose={() => setIsWalletModalOpen(false)}
      />
      {!isFocus ? (
        // Matrix Card Compact Mode
        <>
          <TokenInfoBar
            mint={streamMint}
            onTitleClick={onTitleClick}
            liveVolSol={tapeSummary?.solVolume}
            liveVolUsd={
              tapeSummary?.solVolume ? tapeSummary.solVolume * solPrice : null
            }
            liveMcSol={
              tapeSummary?.latestMcUsd
                ? tapeSummary.latestMcUsd / solPrice
                : null
            }
            liveLiqSol={tapeSummary?.latestLiqSol}
            // liveBuyPct={tapeSummary?.buyPct}
            compact={true}
          />
          {streamMint && <NarrativePanel mint={streamMint!} />}

          <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-1 bg-bg-panel flex items-center justify-between select-none">
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-black uppercase tracking-wider text-[var(--color-fg-dim)]">
                Timeframe
              </span>
              <div className="flex gap-0.5">
                {CHART_INTERVALS.map((iv) => {
                  const active = chartInterval === iv;
                  return (
                    <button
                      key={iv}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setChartInterval(iv)}
                      className={[
                        "rounded px-1.5 py-0.5 font-mono text-[10px] font-bold transition-all",
                        active
                          ? "bg-[#2EA8FF]/20 text-[#2EA8FF] border border-[#2EA8FF]/30"
                          : "text-[var(--color-fg-dim)] hover:text-[var(--color-fg)] border border-transparent",
                      ].join(" ")}
                    >
                      {iv}
                    </button>
                  );
                })}
              </div>
            </div>
            {loading ? (
              <div className="flex items-center gap-1">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                <span className="text-[8px] font-black text-[var(--color-status-success)] uppercase tracking-widest leading-none">
                  Sync
                </span>
              </div>
            ) : null}
          </div>
        </>
      ) : (
        // Main Focus Full Mode - Chart as backdrop with elements inside
        <>
          <div className="relative flex-1 h-full min-h-[500px] overflow-hidden">
            {/* Chart fills full background */}
            <div className="absolute inset-0 z-0 flex flex-col">
              <ErrorBoundary>
                <TokenInfoBar
                  mint={streamMint}
                  onTitleClick={onTitleClick}
                  liveVolSol={tapeSummary?.solVolume}
                  liveVolUsd={
                    tapeSummary?.solVolume
                      ? tapeSummary.solVolume * solPrice
                      : null
                  }
                  liveMcSol={
                    tapeSummary?.latestMcUsd
                      ? tapeSummary.latestMcUsd / solPrice
                      : null
                  }
                  liveLiqSol={tapeSummary?.latestLiqSol}
                  compact={isMobile}
                  // liveBuyPct={tapeSummary?.buyPct}
                />
                {streamMint && <NarrativePanel mint={streamMint!} />}
              </ErrorBoundary>

              {/* MOBILE TIMEFRAME & PRICE/MC TOOLBAR */}
              {isMobile && (
                <div className="flex items-center justify-between px-3 py-1.5 bg-[#0b0e14] border-b border-white/10 select-none shrink-0 overflow-x-auto no-scrollbar z-20 relative">
                  <div className="flex items-center gap-1 shrink-0">
                    {CHART_INTERVALS.map((iv) => {
                      const active = chartInterval === iv;
                      return (
                        <button
                          key={iv}
                          type="button"
                          onClick={() => setChartInterval(iv)}
                          className={[
                            "rounded px-2 py-0.5 font-mono text-[11px] font-bold transition-all whitespace-nowrap",
                            active
                              ? "bg-white text-black font-black shadow"
                              : "text-white/60 hover:text-white bg-transparent",
                          ].join(" ")}
                        >
                          {iv}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-2 pl-2 border-l border-white/10 shrink-0 ml-auto">
                    <button
                      type="button"
                      onClick={() => setYMcCap(!yMcCap)}
                      className="rounded px-2 py-0.5 font-mono text-[10px] font-black bg-white/10 text-white hover:bg-white/20 transition-all border border-white/10"
                    >
                      {yMcCap ? "Price / MC" : "MC / Price"}
                    </button>
                  </div>
                </div>
              )}

              {/* Chart container */}
              <div className="relative flex-1 min-h-0 h-full">
                {/* Non-blocking live syncing pill when candles are already displayed */}
                {loading && chartRows.length > 0 && (
                  <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bg-panel/90 border border-emerald-500/30 backdrop-blur-md text-[10px] font-mono text-emerald-400 pointer-events-none shadow-lg animate-pulse">
                    <span className="size-1.5 rounded-full bg-emerald-400 animate-ping" />
                    <span>Syncing feed...</span>
                  </div>
                )}

                {/* Blocking Loading Overlay - ONLY shown when chart has zero candles */}
                {loading && chartRows.length === 0 && (
                  <div 
                    onClick={() => setLoading(false)}
                    className="absolute inset-0 z-10 flex items-center justify-center bg-bg-deep/60 backdrop-blur-[2px] transition-opacity duration-300 cursor-pointer group"
                    title="Click to dismiss overlay"
                  >
                    <div className="flex flex-col items-center">
                      <div className="relative size-16 mb-4">
                        <div className="absolute inset-0 rounded-full border-2 border-emerald-500/20" />
                        <div className="absolute inset-0 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
                        <Zap className="absolute inset-0 m-auto size-6 text-[var(--color-status-success)] animate-pulse" />
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-[11px] font-black uppercase tracking-[0.2em] text-[var(--color-status-success)]">
                          Syncing Feed
                        </span>
                        <span className="text-[9px] font-mono text-[var(--color-fg-dim)] animate-pulse">
                          Requesting Pump Candles...
                        </span>
                        <span className="text-[8px] font-mono text-emerald-400/60 opacity-0 group-hover:opacity-100 transition-opacity mt-1">
                          Click to dismiss
                        </span>
                      </div>
                    </div>
                    {/* Scanning Scanline */}
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                      <div className="w-full h-[1px] bg-emerald-500/10 shadow-[0_0_15px_rgba(16,185,129,0.2)] animate-scanline" />
                    </div>
                  </div>
                )}

                <div
                  className="relative min-h-0 flex-1 h-full"
                  style={{
                    minHeight: localHeight ? `${localHeight}px` : undefined,
                  }}
                >
                  {/* Zoom HUD - Top Left */}
                  {!isMobile && (
                    <div className="absolute top-4 left-4 z-[30] pointer-events-none flex gap-1.5 opacity-80">
                      <div className="bg-bg-panel/80 backdrop-blur-md px-2 py-0.5 rounded border border-border-subtle flex items-baseline gap-1 shadow-lg">
                        <span className="text-[8px] font-black text-[var(--color-fg-dim)] uppercase tracking-tight">
                          Spacing
                        </span>
                        <span className="text-[10px] font-mono font-bold text-[var(--color-status-success)]">
                          {zoomStats.barSpacing.toFixed(1)}
                        </span>
                      </div>
                      <div className="bg-bg-panel/80 backdrop-blur-md px-2 py-0.5 rounded border border-border-subtle flex items-baseline gap-1 shadow-lg">
                        <span className="text-[8px] font-black text-[var(--color-fg-dim)] uppercase tracking-tight">
                          Offset
                        </span>
                        <span className="text-[10px] font-mono font-bold text-[#2EA8FF]">
                          {zoomStats.rightOffset}
                        </span>
                      </div>
                      <div className="bg-bg-panel/80 backdrop-blur-md px-2 py-0.5 rounded border border-border-subtle flex items-baseline gap-1 shadow-lg pointer-events-auto">
                        <span className="text-[8px] font-black text-[var(--color-fg-dim)] uppercase tracking-tight">
                          Height
                        </span>
                        <input
                          type="number"
                          placeholder="Auto"
                          value={localHeight}
                          onChange={(e) =>
                            setLocalHeight(
                              e.target.value ? Number(e.target.value) : "",
                            )
                          }
                          className="w-10 bg-transparent text-[var(--color-status-success)] font-mono font-bold text-[10px] focus:outline-none placeholder:text-[var(--color-status-success)]/30"
                        />
                      </div>
                    </div>
                  )}

                  {/* Timeframe Selector - Top Left below Zoom */}
                  {!isMobile && (
                    <div className="absolute top-16 left-4 z-[30] flex items-center gap-2">
                      <div className="flex gap-1 flex-wrap max-w-[250px]">
                        {CHART_INTERVALS.map((iv) => {
                          const active = chartInterval === iv;
                          return (
                            <button
                              key={iv}
                              type="button"
                              aria-pressed={active}
                              onClick={() => setChartInterval(iv)}
                              className={[
                                "rounded-lg border px-2 py-0.5 font-mono text-[10px] transition-colors font-bold whitespace-nowrap",
                                active
                                  ? "border-[color-mix(in_srgb,var(--color-fg)_18%,transparent)] bg-[color-mix(in_srgb,#2EA8FF_14%,transparent)] text-[#2EA8FF]"
                                  : "border-[var(--color-border)] bg-transparent text-[var(--color-fg-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-fg)]",
                              ].join(" ")}
                            >
                              {iv}
                            </button>
                          );
                        })}
                        
                        <div className="w-px h-4 bg-border-subtle mx-1 self-center" />
                        <button
                          type="button"
                          aria-pressed={showFvg}
                          onClick={() => setShowFvg(f => !f)}
                          className={[
                            "rounded-lg border px-2 py-0.5 font-mono text-[10px] transition-colors font-bold whitespace-nowrap",
                            showFvg
                              ? "border-[color-mix(in_srgb,var(--color-fg)_18%,transparent)] bg-[color-mix(in_srgb,#8b5cf6_14%,transparent)] text-purple-400"
                              : "border-[var(--color-border)] bg-transparent text-[var(--color-fg-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-fg)]",
                          ].join(" ")}
                        >
                          {t("chart.fvg")}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Search Input - Top Center */}
                  {/* <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[30] w-full px-4">
                    <div className="relative flex items-center w-full max-w-[280px] mx-auto">
                      <input
                        id="ca-mint-input"
                        className="unt-input h-8 min-h-8 w-full pl-8 pr-2.5 py-1.5 font-mono text-[11px] leading-tight border border-border-subtle bg-bg-panel/80 backdrop-blur-md focus:border-[#2EA8FF]/40 focus:bg-bg-deep/60 transition-all rounded-lg shadow-lg"
                        placeholder="Paste mint to chart..."
                        title="Paste a Solana/Pump.fun mint — chart and tape load automatically."
                        value={caMintInput}
                        onChange={(e) => setCaMintInput(e.target.value)}
                        spellCheck={false}
                        autoCapitalize="off"
                        autoCorrect="off"
                        aria-label="Contract address (mint)"
                      />
                      <svg
                        className="absolute left-2.5 size-3.5 text-fg-muted/50"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div> */}

                  {/* Security HUD - Top Right */}
                  <div className="absolute top-4 right-4 z-[30] pointer-events-none flex flex-col items-end gap-1.5">
                    {(() => {
                      const snap = nursery.getSnapshot();
                      const all = snap.visibleTokens || [];
                      const token = all.find((t) => t.mint === caMintInput);
                      if (!token) return null;

                      const whaleCount = (token as any).whaleCount || 0;
                      const isRugged =
                        !!(token as any).complete &&
                        (token as any).marketCapSol < 1;

                      return (
                        <>
                          {isRugged && (
                            <div className="bg-rose-500/20 backdrop-blur-md px-2 py-1 rounded border border-rose-500/30 flex items-center gap-1.5 shadow-lg animate-pulse">
                              <ShieldCheck className="size-2.5 text-[var(--color-status-danger)]" />
                              <span className="text-[9px] font-black text-[var(--color-status-danger)] uppercase tracking-widest">
                                {t("chart.rugged")}
                              </span>
                            </div>
                          )}

                          {whaleCount > 0 && (
                            <div className="bg-amber-500/10 backdrop-blur-md px-2 py-0.5 rounded border border-amber-500/20 flex items-center gap-1.5 shadow-lg">
                              <Target className="size-2.5 text-amber-500" />
                              <span className="text-[10px] font-bold text-amber-500">
                                {whaleCount}W
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Center HUD Controls */}
                 
                </div>

                  </div>
                </div>

                <div
                  ref={hostRef}
                  className={`absolute inset-0 transition-opacity duration-500 ${loading ? "opacity-20 grayscale-[0.5]" : "opacity-100"}`}
                />
                
                {/* Custom FVG Overlay */}
                <div 
                  ref={fvgContainerRef}
                  className="absolute inset-0 z-10 pointer-events-none overflow-hidden"
                >
                  {fvgs.map(fvg => (
                    <div
                      key={fvg.id}
                      data-id={fvg.id}
                      data-start={fvg.startTime}
                      data-end={fvg.endTime || ""}
                      data-top={fvg.top}
                      data-bottom={fvg.bottom}
                      className={`absolute opacity-20 ${fvg.type === 'bullish' ? 'bg-emerald-500 border-l border-emerald-400' : 'bg-rose-500 border-l border-rose-400'} mix-blend-screen transition-opacity duration-300`}
                      style={{ display: 'none' }}
                    />
                  ))}
                </div>
                
                {/* Hardware-accelerated HTML Profile Avatars Layer */}
                <div
                  ref={markersContainerRef}
                  className="absolute inset-0 z-20 pointer-events-none"
                >
                  {renderableGroups.map((g) => (
                    <div
                      key={g.id}
                      data-time={g.time}
                      data-price={g.price}
                      data-isbuy={g.isBuy}
                      className={`absolute left-0 top-0 flex items-center justify-center transition-opacity duration-150 will-change-transform ${g.isBuy ? 'flex-col' : 'flex-col-reverse'}`}
                      style={{ opacity: 0 }}
                    >
                      {/* Pointer Stem connecting to the exact price point on the candle */}
                      <div className={`w-0.5 h-6 ${g.isBuy ? 'bg-[#00FFA3]/80 bg-gradient-to-t from-[#00FFA3]/80 to-transparent' : 'bg-[#FF3358]/80 bg-gradient-to-b from-[#FF3358]/80 to-transparent'}`} />

                      <div className="relative flex justify-center items-center">
                        {g.wallets.map((w: any, i: number) => {
                          const pic =
                            w.profile?.image ||
                            w.profile?.avatar ||
                            w.profile?.imageUrl ||
                            w.profile?.avatarUrl ||
                            `https://api.dicebear.com/7.x/identicon/svg?seed=${w.id}`;
                          
                          return (
                            <div
                              key={w.id}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const fallbackProfile = w.profile || {
                                  address: w.trader || "Unknown",
                                  username: `Trader ${(w.trader || "").slice(0, 4)}...${(w.trader || "").slice(-4)}`,
                                  avatar: pic,
                                  solBalance: 0,
                                  tokenCount: 1,
                                  isWhale: w.solAmount >= 5,
                                };
                                setTooltipMarker({
                                  marker: { ...w, profile: fallbackProfile },
                                  position: { x: rect.left + rect.width / 2, y: rect.top },
                                });
                              }}
                              onMouseLeave={() => setTooltipMarker(null)}
                              className={`relative size-8 rounded-full overflow-hidden border-2 shadow-[0_0_15px_rgba(0,0,0,0.8)] pointer-events-auto cursor-pointer transition-transform hover:scale-125 ${
                                i > 0 ? "-ml-3" : ""
                              } ${
                                g.isBuy
                                  ? "border-[#00FFA3] bg-[#00FFA3]/10 shadow-[#00FFA3]/30"
                                  : "border-[#FF3358] bg-[#FF3358]/10 shadow-[#FF3358]/30"
                              }`}
                              style={{ zIndex: g.wallets.length - i }}
                            >
                              <img src={pic} className="w-full h-full object-cover" alt="trader profile" />
                            </div>
                          );
                        })}

                        {/* SOL Amount Badge */}
                        <div className={`absolute ${g.isBuy ? 'top-full mt-1.5' : 'bottom-full mb-1.5'} left-1/2 -translate-x-1/2 whitespace-nowrap`}>
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono font-black border backdrop-blur-md shadow-lg flex items-center gap-0.5 ${
                            g.isBuy 
                              ? "bg-[#00FFA3]/20 text-[#00FFA3] border-[#00FFA3]/30" 
                              : "bg-[#FF3358]/20 text-[#FF3358] border-[#FF3358]/30"
                          }`}>
                            {g.wallets.reduce((acc: number, w: any) => acc + w.solAmount, 0).toFixed(1)} SOL
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Bottom Left Quick Trade */}
              {caMintInput && (
                <div className={`absolute ${isMobile ? "bottom-[120px]" : "bottom-6"} left-4 z-[30]`}>
                  <ChartQuickTrade mint={caMintInput} tradingMode={tradingMode} />
                </div>
              )}

              {/* Standalone Airdrop / Connect Wallet Button */}
              <div className={`absolute ${isMobile ? "bottom-[120px]" : "bottom-6"} right-4 z-[30] flex items-center gap-2`}>
                {caMintInput && <ExtractAirdropPopover caMintInput={caMintInput} />}
                {!connected || !publicKey ? (
                  <button 
                    type="button"
                    onClick={() => setIsWalletModalOpen(true)}
                    className="bg-black/60 backdrop-blur-md border border-white/10 px-3 py-2 rounded text-[10px] font-bold text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    Connect Wallet
                  </button>
                ) : null}
              </div>
        </>
      )}

      {error ? (
        <div className="shrink-0 border-b border-[var(--color-border)] px-4 py-2 text-[14px] text-[var(--color-danger)]">
          {error}
        </div>
      ) : null}
      {liveErr && streamMint && !error ? (
        <div className="shrink-0 border-b border-[var(--color-border)] px-4 py-2 text-[13px] text-amber-300/95">
          PumpPortal: {liveErr}
        </div>
      ) : null}





      {/* Trader Profile Tooltip */}
      {tooltipMarker && tooltipMarker.marker.profile && (
        <TraderProfileTooltip
          profile={tooltipMarker.marker.profile}
          solAmount={tooltipMarker.marker.solAmount}
          isBuy={tooltipMarker.marker.isBuy}
          position={tooltipMarker.position}
        />
      )}
    </div>
  );
}
